import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";
import {
  Crown,
  User,
  Flame,
  HeartPulse,
  Sparkles,
  RefreshCw,
  Check,
  Lock,
  Gavel,
  X,
  Coins,
  Wand2,
  Users,
  Package,
  Play,
  Swords,
} from "lucide-react";


// ─── Modules extraits ──────────────────────────────────────────────────────
import type { Player, Item } from "./types/game";
import type { PlayerRow } from "./components/Classement";
import { sb, SB_URL, SBH, loadName, saveName, saveCache, loadCache, saveResultsCache, loadResultsCache, saveMatchesCache, loadMatchesCache, saveLastDerived, loadLastDerived } from "./lib/supabase";
import { getSession, signInGuest, signInWithProvider, linkIdentityWithProvider, parseHashError, popAuthFlow, popPendingGuestProfileId, signOut as authSignOut, ensureProfile, updateProfileNickname, popPendingNickname, setPendingNickname, getGuestProfileId, setGuestProfileId, clearGuestProfileId, loadSessionBackup, clearSessionBackup, restoreSessionBackup } from "./lib/auth";
import type { Provider, AuthFlow } from "./lib/auth";
import { Login } from "./components/Login";
import { NicknameSetup } from "./components/NicknameSetup";
import { RecoverAccount } from "./components/RecoverAccount";
import { fetchLiveOdds } from "./lib/oddsApi";
import { fetchWC26Data } from "./lib/wc26Api";
import { C } from "./constants/design";
import { SELL_PRICE, INVENTORY_IMGS, BACKGROUND_IMGS, PP_IMGS, LAYER_IMGS, INAMES, PENDING_CAP, inventoryIsFull, coffreIsFull, COFFRE_CHEST_COST_TICKETS, MAX_COFFRE_CHESTS } from "./constants/items";
import { FeedbackModal } from "./components/ui/FeedbackModal";
import { STARTER_LEAGUE_GOLD } from "./constants/cards";
import {
  MATCHES, M_BY_ID, DEF_RES, STD_ODDS, PREM_ODDS, FAM_ODDS,
  STARTER_GOLD, PACK_STD, PACK_PREM, PACK_FAM,
} from "./constants/matches";
import { iPower, mkItem, rollRar, debugItemGen, VITALITY_PER_LEVEL } from "./engine/itemEngine";
(window as any).debugItemGen = debugItemGen;
import { cStats, cDerived, runSim, playerLevel, levelProgress, getBetPoints } from "./engine/playerEngine";
import { Toast, Coin, SH } from "./components/ui/atoms";
import { Pronos } from "./components/Pronos";
import { Classement, PlayerInspectModal } from "./components/Classement";
import { Packs, RevealModal } from "./components/Packs";
import { LeagueWidget } from "./components/league/LeagueWidget";
import { Lobby, AppTopBar } from "./components/Lobby";
import { Profil } from "./components/Profil";
import { LevelUpScreen } from "./components/ui/LevelUpScreen";
import { BetResultScreen, BetResultData } from "./components/ui/BetResultScreen";
import { pointsForLevel } from "./engine/playerEngine";
import { useImagePreloader } from "./hooks/useImagePreloader";
import { useI18n } from "./i18n/I18nContext";

const TICKET_ICON = "https://i.postimg.cc/d3sX5KSp/ticket-foot-viol-(1).png";


const koTime = (m: { koUTC: string }) => new Date(m.koUTC).getTime();
const isLocked = (m: { id: string; koUTC: string; status?: string }, res: Record<string, any>) =>
  !!res[m.id] ||
  (m.status ? m.status !== "scheduled" : Date.now() >= koTime(m));
const mergeRes = (raw: any[]) => {
  const o: Record<string, { o: string; s: string }> = {};
  (raw || []).forEach((r) => {
    o[r.match_id] = { o: r.outcome, s: r.score };
  });
  return { ...DEF_RES, ...o };
};




/* ─── APP ─────────────────────────────────────────────────────────────── */
export default function App() {
  const [me, setMe] = useState<Player | null>(() => {
    const name = loadName();
    if (!name) return null;
    const { player } = loadCache();
    return player?.name === name ? player : null;
  });
  const [bets, setBets] = useState<Record<string, string>>(() => {
    const name = loadName();
    if (!name) return {};
    const { bets: cachedBets } = loadCache();
    return cachedBets || {};
  });
  const [results, setResults] = useState<Record<string, { o: string; s: string }>>(
    () => loadResultsCache() ?? DEF_RES
  );
  // File d'attente des overlays : on n'affiche qu'un écran à la fois.
  // Les résultats de paris (qui ont fait gagner l'XP) passent AVANT le level-up.
  type Overlay =
    | { kind: "bet"; items: BetResultData[] }
    | { kind: "levelup"; level: number; gotTicket: boolean; vitalityGain?: number };
  const [overlayQueue, setOverlayQueue] = useState<Overlay[]>([]);
  const enqueueOverlay = useCallback((o: Overlay) => setOverlayQueue((q) => [...q, o]), []);
  const closeOverlay = useCallback(() => setOverlayQueue((q) => q.slice(1)), []);
  const seenBetResultsRef = useRef<Set<string>>(
    new Set(JSON.parse(sessionStorage.getItem("seen_bet_results") ?? "[]"))
  );
  // Protège claim_level_rewards contre les appels concurrents (multi-tab, React StrictMode)
  const claimingLevelRef = useRef(false);
  // Refs stables pour loadPlayers — évite de recréer le callback quand me/bets/results changent
  const meRef = useRef<typeof me>(null as any);
  const betsRef = useRef<typeof bets>({});
  const predScoresRef = useRef<typeof predScores>({});
  const mByIdRef = useRef<typeof mById>({});
  const resultsRef = useRef<typeof results>({});
  const [players, setPlayers] = useState<any[]>([]);
  const [playersLoaded, setPlayersLoaded] = useState(false);
  const [matches, setMatches] = useState(() => loadMatchesCache() ?? MATCHES);
  const [tab, setTab] = useState("lobby");
  // Deep-link Lobby → écran de jeu dans LeagueWidget (consommé après application).
  const [leagueEntryView, setLeagueEntryView] = useState<"trial" | "duel" | null>(null);
  // Deep-link Lobby → onglet ciblé du Classement (consommé après application).
  const [classementFocus, setClassementFocus] = useState<"picks" | "duel" | "stadium" | null>(null);
  const [boot, setBoot] = useState(true);
  const [needsNickname, setNeedsNickname] = useState(false);
  // Récupération d'identité : le login Google a atterri sur un user sans compte
  // alors que le device revendique un player existant (fork guest / placeholder).
  const [recoverName, setRecoverName] = useState<string | null>(null);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [isGuestUser, setIsGuestUser] = useState(false);

  // ── Flags d'hydration — empêchent derived de tourner avec des données incomplètes ──
  const [playerLoaded, setPlayerLoaded] = useState(() => {
    const name = loadName();
    if (!name) return false;
    const { player } = loadCache();
    return !!(player?.name === name);
  });
  const [betsLoaded, setBetsLoaded] = useState(() => {
    const name = loadName();
    if (!name) return false;
    const { bets: b } = loadCache();
    return b !== null;
  });
  const [resultsLoaded, setResultsLoaded] = useState(() => loadResultsCache() !== null);
  const [matchesLoaded, setMatchesLoaded] = useState(() => loadMatchesCache() !== null);
  const [playerSource, setPlayerSource] = useState<"cache" | "supabase" | "sync-derived" | "new-player">(() => {
    const name = loadName();
    if (!name) return "cache";
    const { player } = loadCache();
    return player?.name === name ? "cache" : "cache";
  });

  const [profileId, setProfileId] = useState<string | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [reveal, setReveal] = useState<{ items: any[]; kind: string } | null>(null);
  // Mailbox : modal "inventaire plein" quand un gain a été parké dans pending_items.
  const [pendingModal, setPendingModal] = useState(false);
  // Le nag ne se déclenche qu'UNE fois par chargement d'app (pas à chaque vente).
  const pendingNaggedRef = useRef(false);
  const isPurchasing = React.useRef(false);
  const [toast, setToast] = useState<string | null>(null);
  const [refScores, setRefScores] = useState<Record<string, any>>({}); // scores arbitre — persistent entre onglets
  const [predScores, setPredScores] = useState<Record<string, any>>({}); // scores de prono user — persistent entre onglets
  const [, tick] = useState(0);
  const [inspecting, setInspecting] = useState<PlayerRow | null>(null);
  const [inspectLoading, setInspectLoading] = useState(false);
  const inspectReq = useRef(0); // jeton anti-race pour les fetchs d'inspection
  const inspectCacheRef = useRef<Record<string, any>>({}); // inventaire/équipement déjà chargés
  // Matchs de la table Supabase (anciens IDs), uniquement pour résoudre le scoring
  // des paris placés AVANT le passage à l'API WC26. N'apparaissent pas dans l'UI.
  const [sbMatchMap, setSbMatchMap] = useState<Record<string, any>>({});
  const mById = useMemo(
    // M_BY_ID (hardcodé) garantit que les anciens IDs sont toujours présents pendant
    // le chargement — évite le flash wallet négatif entre fetchWC26 et sb.matches.
    // Les matchs Supabase puis WC26 (affichés) ont priorité via l'ordre de spread.
    () => ({ ...M_BY_ID, ...sbMatchMap, ...Object.fromEntries(matches.map((m) => [m.id, m])) }),
    [matches, sbMatchMap]
  );

  // Fetch matches : WC26 API en priorité, Supabase en fallback, hardcodé en dernier recours.
  useEffect(() => {
    async function loadMatches() {
      // ── 1. WC26 API (source principale) ──────────────────────────────────
      const wc26 = await fetchWC26Data();
      if (wc26?.matches.length) {
        // Récupère les cotes hardcodées pour les matchs connus (par paire d'équipes)
        const withOdds = wc26.matches.map((m) => {
          const hc = MATCHES.find(
            (h) =>
              (h.t1 === m.t1 && h.t2 === m.t2) ||
              (h.t1 === m.t2 && h.t2 === m.t1)
          );
          return hc ? { ...m, p1: hc.p1, pX: hc.pX, p2: hc.p2 } : m;
        });
        const sorted = [...withOdds].sort((a, b) =>
          a.koUTC.localeCompare(b.koUTC)
        );
        setMatches(sorted);
        saveMatchesCache(sorted);
        setMatchesLoaded(true);

        // Injecte les résultats WC26 dans l'état + persiste dans le cache
        // (le poll Supabase ne sauvegarde que ses propres IDs — les IDs WC26 doivent
        // être explicitement cachés ici pour éviter earned=0 au refresh suivant).
        if (wc26.results.length) {
          const fromWC26: Record<string, { o: string; s: string }> = {};
          wc26.results.forEach((r) => {
            fromWC26[r.matchId] = { o: r.o, s: r.s };
          });
          setResults((prev) => ({ ...fromWC26, ...prev }));
          const prevCache = loadResultsCache() ?? {};
          saveResultsCache({ ...fromWC26, ...prevCache }); // prev override WC26 (même logique que setResults)
        }

        // Injecte aussi les matchs Supabase (anciens IDs) — même source que le
        // Classement — pour que Profil et Classement résolvent les mêmes paris.
        const sbMatches = await sb.get("matches?select=*");
        if (sbMatches?.length) {
          const fromSb: Record<string, { o: string; s: string }> = {};
          const scoreMap: Record<string, any> = {};
          sbMatches.forEach((r: any) => {
            const hc = MATCHES.find(
              (m) =>
                (m.t1 === r.team1 && m.t2 === r.team2) ||
                (m.t1 === r.team2 && m.t2 === r.team1)
            );
            scoreMap[r.id] = {
              id: r.id, t1: r.team1, t2: r.team2,
              p1: hc?.p1 || 0.4, pX: hc?.pX || 0.27, p2: hc?.p2 || 0.33,
            };
            if (r.status === "finished" && r.result) {
              fromSb[r.id] = { o: r.result, s: `${r.team1_score}–${r.team2_score}` };
            }
          });
          setSbMatchMap(scoreMap);
          if (Object.keys(fromSb).length)
            setResults((prev) => ({ ...fromSb, ...prev })); // prev garde les overrides WC26 et manuels
        }

        fetchLiveOdds().then(applyLiveOdds);
        return;
      }

      // ── 2. Fallback : Supabase ────────────────────────────────────────────
      const raw = await sb.get("matches?select=*&order=match_date");
      if (!raw?.length) {
        setMatchesLoaded(true); // hardcodé MATCHES déjà en state
        fetchLiveOdds().then(applyLiveOdds);
        return;
      }

      const seenPairs = new Set(
        raw.map((r: any) => [r.team1, r.team2].sort().join("|"))
      );
      const mapped = raw.map((r: any) => {
        const hc = MATCHES.find(
          (m) =>
            (m.t1 === r.team1 && m.t2 === r.team2) ||
            (m.t1 === r.team2 && m.t2 === r.team1)
        );
        const rawKoUTC = r.match_date ? new Date(r.match_date).toISOString() : "";
        const isMidnight = rawKoUTC.endsWith("T00:00:00.000Z");
        // Prefer hardcoded koUTC over midnight placeholder to avoid locking bets at 09:00 KST.
        const koUTC = (isMidnight && hc?.koUTC) ? hc.koUTC : (rawKoUTC || hc?.koUTC || "");
        if (isMidnight && r.status === "scheduled") {
          console.warn(`[koUTC] midnight placeholder — update DB: ${r.id} (${r.team1} vs ${r.team2}) → using ${koUTC}`);
        }
        const date = new Date(koUTC).toLocaleDateString("en-CA", {
          timeZone: "Asia/Seoul",
        });
        return {
          id: r.id, grp: hc?.grp || "", date, koUTC,
          t1: r.team1, t2: r.team2,
          p1: hc?.p1 || 0.4, pX: hc?.pX || 0.27, p2: hc?.p2 || 0.33,
          status: r.status,
        };
      });

      const hardcodedOnly = MATCHES.filter(
        (m) => !seenPairs.has([m.t1, m.t2].sort().join("|"))
      ).map((m) => ({
        ...m,
        date: new Date(m.koUTC).toLocaleDateString("en-CA", { timeZone: "Asia/Seoul" }),
      }));
      const merged = [...mapped, ...hardcodedOnly].sort((a, b) =>
        a.koUTC.localeCompare(b.koUTC)
      );
      setMatches(merged);
      saveMatchesCache(merged);
      setMatchesLoaded(true);

      const fromMatches: Record<string, { o: string; s: string }> = {};
      raw
        .filter((r: any) => r.status === "finished" && r.result)
        .forEach((r: any) => {
          fromMatches[r.id] = { o: r.result, s: `${r.team1_score}–${r.team2_score}` };
        });
      if (Object.keys(fromMatches).length)
        setResults((prev) => ({ ...prev, ...fromMatches }));

      fetchLiveOdds().then(applyLiveOdds);
    }
    loadMatches();
  }, []);

  const applyLiveOdds = useCallback(
    (oddsData: { t1: string; t2: string; p1: number; pX: number; p2: number }[]) => {
      if (!oddsData.length) return;
      setMatches((prev) =>
        prev.map((m) => {
          const live = oddsData.find(
            (o) =>
              (o.t1 === m.t1 && o.t2 === m.t2) ||
              (o.t1 === m.t2 && o.t2 === m.t1)
          );
          if (!live) return m;
          const flipped = live.t1 !== m.t1;
          return {
            ...m,
            p1: flipped ? live.p2 : live.p1,
            pX: live.pX,
            p2: flipped ? live.p1 : live.p2,
          };
        })
      );
    },
    []
  );

  const flash = useCallback((m: string) => {
    setToast(m);
    setTimeout(() => setToast(null), 1900);
  }, []);
  const updateRefScore = useCallback((mid: string, field: string, val: any) => {
    setRefScores((prev) => ({
      ...prev,
      [mid]: { ...(prev[mid] || {}), [field]: val },
    }));
  }, []);
  const updatePredScore = useCallback((mid: string, field: string, val: any) => {
    setPredScores((prev) => ({
      ...prev,
      [mid]: { ...(prev[mid] || {}), [field]: val },
    }));
  }, []);

  // Tick every 30s for Live badge updates
  useEffect(() => {
    const t = setInterval(() => tick((v) => v + 1), 30000);
    return () => clearInterval(t);
  }, []);

  // Poll match results every 20s
  useEffect(() => {
    const poll = async () => {
      const [rawRes, rawM] = await Promise.all([
        sb.get("match_results?select=*"),
        sb.get("matches?select=id,status,result,team1_score,team2_score"),
      ]);
      const base: Record<string, { o: string; s: string }> = rawRes?.length ? mergeRes(rawRes) : { ...DEF_RES };
      (rawM || [])
        .filter((r) => r.status === "finished" && r.result)
        .forEach((r) => {
          base[r.id] = { o: r.result, s: `${r.team1_score}–${r.team2_score}` };
        });
      // Merge (jamais remplacer) : les résultats WC26/manuels déjà chargés ne doivent pas
      // disparaître si Supabase répond vide → évite le clignotement des points.
      setResults((prev) => {
        const merged = { ...prev, ...base };
        saveResultsCache(merged);
        return merged;
      });
    };
    poll();
    const t = setInterval(poll, 10000);
    return () => clearInterval(t);
  }, []);

  // Boot : localStorage d'abord (immédiat), puis Supabase en arrière-plan
  // Garde anti double-exécution (StrictMode / re-mount) : un 2e boot re-fetche la
  // ligne player AVANT que le patch du sync effect ait atterri → setMe(score périmé).
  const bootRanRef = useRef(false);
  useEffect(() => {
    if (bootRanRef.current) return;
    bootRanRef.current = true;
    (async () => {
      // ── Auth boot ──────────────────────────────────────────────────────────────
      // Reads auth_flow + pendingGuestProfileId from sessionStorage (tab-isolated)
      // to know whether we're returning from a login or a link OAuth redirect.
      //
      // "login" flow: apply pendingNickname to the profile, show NicknameSetup if missing.
      // "link"  flow: verify returnedUserId === pendingGuestProfileId (link succeeded).
      //               If mismatch → show error, do NOT touch profiles/pendingNickname.
      //               If match    → only patch is_guest=false, no nickname change.
      let pname = loadName();
      let bootProfileId: string | null = null;
      const bootGuestProfileId = (() => { try { return localStorage.getItem("pronocup_guest_profile_id"); } catch { return null; } })();

      // Read and consume flow metadata set before the redirect (sessionStorage, tab-isolated)
      const authFlow: AuthFlow | null = popAuthFlow();
      const pendingGuestProfileId: string | null = popPendingGuestProfileId();
      // pendingNickname is only relevant for the "login" flow
      const pendingNickname: string | null = authFlow === "login" ? popPendingNickname() : null;
      // If "login" flow had no pending but there's a leftover, consume and discard it
      if (authFlow !== "login") { try { sessionStorage.removeItem("pending_nickname"); } catch {} }

      // Check for OAuth errors returned in the URL hash
      const hashErr = parseHashError();
      if (hashErr) {
        const isAlreadyLinked = hashErr.code === "identity_already_exists" || hashErr.message.toLowerCase().includes("already");
        setOauthError(
          isAlreadyLinked
            ? "This Google account is already linked to another account. Sign in directly with Google instead."
            : hashErr.message
        );
      }

      console.log("[Auth] boot start", {
        authFlow: authFlow ?? "none",
        pendingGuestProfileId: pendingGuestProfileId ?? "null",
        pendingNickname: pendingNickname ?? "null",
        savedName: pname ?? "null",
        guestProfileIdInStorage: bootGuestProfileId ?? "null",
      });

      try {
        const session = getSession();
        const returnedUserId = session?.user.id ?? null;
        const isAnon = !!session?.user.is_anonymous;

        console.log("[Auth] boot session", {
          authFlow: authFlow ?? "none",
          returnedUserId: returnedUserId ?? "null",
          pendingGuestProfileId: pendingGuestProfileId ?? "null",
          pendingNickname: pendingNickname ?? "null",
          isAnonymous: isAnon,
        });

        if (session) {
          bootProfileId = session.user.id;
          setProfileId(session.user.id);

          if (authFlow === "link") {
            // Verify the link actually attached to the guest's account (same user ID).
            // If Supabase returned a different user (the existing Google account),
            // the link failed silently — do not touch profiles or players.
            if (pendingGuestProfileId && returnedUserId !== pendingGuestProfileId) {
              console.error("[Auth] link flow MISMATCH — returnedUserId differs from pendingGuestProfileId", {
                returnedUserId,
                pendingGuestProfileId,
              });
              setOauthError(
                "Could not link Google account: this Google account is already associated with another account. Sign in with Google directly instead."
              );
              // Restore the original guest session if we still have it in localStorage
              // (it may have been overwritten by the OAuth redirect — nothing we can do)
              setBoot(false);
              return;
            }
            // Link succeeded: same user ID, is_anonymous is now false.
            // Only update is_guest flag — do NOT rename the nickname.
            console.log("[Auth] link flow SUCCESS — patching is_guest=false, keeping existing nickname");
            const profile = await ensureProfile(session, "", false);
            if (profile?.nickname) {
              pname = profile.nickname;
              saveName(pname);
              setIsGuestUser(false);
              clearGuestProfileId();
            }
          } else {
            // "login" flow (or cold boot with existing session — no authFlow set)
            const profile = await ensureProfile(session, pendingNickname ?? "", isAnon);
            if (profile?.nickname) {
              pname = profile.nickname;
              saveName(pname);
              setIsGuestUser(isAnon);
            } else if (!isAnon) {
              if (authFlow === "login") {
                // Login atterri sur un user Google SANS profil. Avant d'envoyer vers
                // la création de compte, vérifier si le device revendique déjà un
                // player existant jamais lié à ce user (fork guest / placeholder du
                // batch 03/07 — incidents SJ/camille/Luka 07/2026) → récupération.
                if (pname) {
                  const claimed = await sb.get(
                    `players?name=eq.${encodeURIComponent(pname)}&is_bot=eq.false&limit=1`
                  );
                  if (claimed[0] && claimed[0].profile_id !== session.user.id) {
                    console.warn("[Auth] login on profile-less user, device claims existing player — offering recovery", {
                      name: claimed[0].name,
                      existing_profile_id: claimed[0].profile_id ?? "null",
                      google_user: session.user.id,
                    });
                    setRecoverName(claimed[0].name);
                    setBoot(false);
                    return;
                  }
                }
                // New OAuth user with no nickname stored → show the nickname picker
                setNeedsNickname(true);
                setBoot(false);
                return;
              }
              // Cold boot: stale or expired non-anonymous session with no recoverable profile
              // → purge it so the Login screen shows instead of the nickname picker
              authSignOut();
            }
          }
        }
      } catch (e) {
        console.warn("[Auth] boot auth error:", e);
      }

      if (pname) {
        // 1. Cache local immédiat (évite le re-ask du blaze)
        const { player: cached, bets: cachedBets } = loadCache();
        if (cached && cached.name === pname) {
          setMe(cached);
          setBets(cachedBets);
        }

        // 2. Sync Supabase (source de vérité) — prefer profile_id lookup when available
        const enc = encodeURIComponent(pname);
        const playerQuery = bootProfileId
          ? `players?profile_id=eq.${bootProfileId}&limit=1`
          : `players?name=eq.${enc}&limit=1`;
        console.log("[GuestAuth] boot player query", {
          method: bootProfileId ? "profile_id" : "name (legacy/no-auth)",
          query: playerQuery,
        });
        const [rows, rawRes] = await Promise.all([
          sb.get(playerQuery),
          sb.get("match_results?select=*"),
        ]);
        // Les bets sont clés par player_name : utiliser le nom de la ligne player
        // réellement chargée (par profile_id), jamais le nickname du profil — s'ils
        // divergent (re-link manuel), on chargerait les paris d'un autre joueur et
        // le sync effect écraserait score/wincount/betcount avec ces mauvaises données.
        const betsName = rows[0]?.name ?? pname;
        const rawBets = await sb.get(
          `bets?player_name=eq.${encodeURIComponent(betsName)}&select=match_id,pick,score_a,score_b`
        );
        if (rows[0]) {
          const freshBets = Object.fromEntries(
            rawBets.map((b) => [b.match_id, b.pick])
          );
          const freshPreds: Record<string, { a: string; b: string }> = {};
          rawBets.forEach((b) => {
            if (b.score_a != null)
              freshPreds[b.match_id] = {
                a: String(b.score_a),
                b: String(b.score_b ?? ""),
              };
          });
          // Merge : garde le score du cache s'il est plus élevé que la DB (le sync
          // effect persiste en localStorage mais pas toujours en Supabase).
          const cachedTickets = cached?.name === pname ? (cached?.tickets || 0) : 0;
          const mergedPlayer = {
            ...rows[0],
            score: Math.max(
              rows[0].score || 0,
              cached?.name === pname ? (cached?.score || 0) : 0
            ),
            bonus_points: Math.max(
              rows[0].bonus_points || 0,
              cached?.name === pname ? (cached?.bonus_points || 0) : 0
            ),
            // tickets : pas de colonne Supabase pour l'instant → localStorage fait foi.
            // Dès que la colonne existe, rows[0].tickets prend le dessus automatiquement.
            tickets: Math.max(rows[0].tickets || 0, cachedTickets),
          };
          console.log("[GuestAuth] boot player loaded", {
            name: mergedPlayer.name,
            profile_id: (mergedPlayer as any).profile_id ?? "null",
            loaded_by: bootProfileId ? "profile_id" : "name",
          });
          // Self-heal : si le nickname du profil diverge du nom de la ligne player
          // (ex: profile_id re-linké manuellement), la ligne player fait foi.
          // Sans ça, saveName(nickname) fait basculer les boots suivants (sans session
          // résolue) sur le MAUVAIS compte via le lookup par nom.
          if (pname !== mergedPlayer.name) {
            console.warn("[Auth] profile nickname ≠ player name — realigning", {
              profileNickname: pname,
              playerName: mergedPlayer.name,
            });
            saveName(mergedPlayer.name);
            // JWT utilisateur obligatoire : la RLS de profiles n'autorise que le
            // propriétaire — un patch avec la clé anon échouerait silencieusement.
            const healSession = getSession();
            if (bootProfileId && healSession)
              updateProfileNickname(healSession, mergedPlayer.name);
          }
          console.log("[DailyChest] raw player from boot", {
            name: mergedPlayer.name,
            duel_chest_date: mergedPlayer.duel_chest_date,
            duel_chest_matches: mergedPlayer.duel_chest_matches,
            duel_chest_claimed: mergedPlayer.duel_chest_claimed,
          });
          setMe(mergedPlayer);
          setPlayerSource("supabase");
          setBets(freshBets);
          setPredScores(freshPreds);
          saveCache(mergedPlayer, freshBets);
          setPlayerLoaded(true);
          setBetsLoaded(true);
        } else {
          // No player found by profile_id — check by name before creating to avoid duplicates.
          console.warn("[GuestAuth] boot — no player found by profile_id, checking by name", {
            query: playerQuery,
            profile_id: bootProfileId ?? "null",
            name: pname,
          });
          // ilike : cohérent avec createPlayer, et matche les variantes de casse
          // ('luka' vs 'Luka') au lieu de créer un doublon quasi identique.
          const byNameRows = bootProfileId
            ? await sb.get(`players?name=ilike.${encodeURIComponent(pname)}&limit=1`)
            : [];
          console.log("[GuestAuth] boot — name fallback result", {
            name: pname,
            found: byNameRows[0]
              ? `yes (profile_id=${byNameRows[0].profile_id ?? "null"})`
              : "none",
          });
          if (byNameRows[0] && byNameRows[0].profile_id && byNameRows[0].profile_id !== bootProfileId) {
            // Le pseudo vient du localStorage du device (loadName) mais sa ligne
            // player pointe un AUTRE profile_id. Jamais adopter silencieusement
            // (deux comptes partageraient un player → savePlayer écraserait les
            // items/golds de l'autre). On propose la récupération explicite : le
            // device qui détient ce nom en storage est très probablement son
            // propriétaire dont l'identité a forké (incidents 07/2026).
            const recoverable = !getSession()?.user.is_anonymous;
            console.warn("[GuestAuth] boot — name exists with DIFFERENT profile_id", {
              name: pname,
              existing_profile_id: byNameRows[0].profile_id,
              boot_profile_id: bootProfileId,
              action: recoverable ? "offering recovery" : "sending to NicknameSetup (anon session)",
            });
            // Récupération réservée aux sessions Google/email : adopter un player
            // sur un user anonyme re-forkerait l'identité au prochain login.
            if (recoverable) setRecoverName(byNameRows[0].name);
            else setNeedsNickname(true);
            setBoot(false);
            return;
          }
          if (byNameRows[0]) {
            // Legacy row exists — stamp profile_id and reuse, never create a duplicate
            const existing = byNameRows[0];
            if (!existing.profile_id && bootProfileId) {
              console.log("[GuestAuth] boot — stamping profile_id on legacy player", { name: pname, bootProfileId });
              await sb.patch("players", `name=eq.${encodeURIComponent(existing.name)}`, { profile_id: bootProfileId });
              existing.profile_id = bootProfileId;
            }
            saveCache(existing, {});
            setMe(existing);
            setPlayerSource("supabase");
            setBets({});
            setPlayerLoaded(true);
            setBetsLoaded(true);
          } else if (bootProfileId) {
            // Truly new player with known profile_id — safe to create
            console.warn("[GuestAuth] boot — no player found by name either, creating new player row", {
              name: pname,
              profile_id: bootProfileId,
            });
            const starter = mkItem(rollRar(STD_ODDS));
            if (!starter) { console.error("[init] Failed to create starter item"); return; }
            const payload: Record<string, unknown> = {
              name: pname, profile_id: bootProfileId,
              spent: 0, score: 0, wincount: 0, betcount: 0,
              inventory: [starter], equipped: { [starter.slot]: starter.iid },
            };
            console.log("[GuestAuth] create player payload", { name: payload.name, profile_id: payload.profile_id });
            await sb.insert("players", payload);
            saveCache(payload as any, {});
            setMe(payload as any);
            setPlayerSource("new-player");
            setBets({});
            setPlayerLoaded(true);
            setBetsLoaded(true);
          } else {
            // No profile_id and no player found — cannot safely create a real player row
            console.error("[GuestAuth] boot — cannot create player: no profile_id available", { name: pname });
            setBoot(false);
            return;
          }
        }
        // Merge — ne jamais remplacer l'état courant : on perdrait les résultats WC26 déjà en state.
        if (rawRes?.length) {
          setResults((prev) => {
            const merged = { ...prev, ...mergeRes(rawRes) }; // Supabase a priorité sur WC26
            saveResultsCache(merged);
            return merged;
          });
        }
        setResultsLoaded(true);
      }
      setBoot(false);
    })();
  }, []);

  // Rechargement des paris depuis Supabase au retour sur l'onglet pronos
  const meName = me?.name;
  useEffect(() => {
    if (tab !== "pronos" || !meName) return;
    const enc = encodeURIComponent(meName);
    sb.get(
      `bets?player_name=eq.${enc}&select=match_id,pick,score_a,score_b`
    ).then((raw) => {
      if (!raw?.length) return;
      const fresh = Object.fromEntries(raw.map((b) => [b.match_id, b.pick]));
      const freshPreds: Record<string, { a: string; b: string }> = {};
      raw.forEach((b) => {
        if (b.score_a != null)
          freshPreds[b.match_id] = {
            a: String(b.score_a),
            b: String(b.score_b ?? ""),
          };
      });
      setBets((prev) => {
        const merged = { ...fresh, ...prev };
        saveCache(me, merged);
        return merged;
      });
      setPredScores((prev) => ({ ...freshPreds, ...prev }));
    });
  }, [tab, meName]); // eslint-disable-line

  // Sync refs — toujours à jour sans recréer les callbacks qui en dépendent
  meRef.current = me;
  betsRef.current = bets;
  predScoresRef.current = predScores;
  mByIdRef.current = mById;
  resultsRef.current = results;

  const betIds = Object.keys(bets || {});
  const hasBets = betIds.length > 0;
  const hasMatchesForBets = hasBets ? betIds.every((mid) => !!mById[mid]) : true;

  // Un match est "passé" si son statut indique qu'il est terminé, ou si le KO + 3h est dépassé.
  const nowMs = Date.now();
  const isPastMatch = (mid: string): boolean => {
    const m = mById[mid] as any;
    if (!m) return false;
    if (m.status === "finished" || m.status === "live") return true;
    if (m.koUTC) {
      const ko = new Date(m.koUTC).getTime();
      return Number.isFinite(ko) && ko + 3 * 60 * 60 * 1000 < nowMs;
    }
    return false;
  };
  const requiredResultIds = betIds.filter(isPastMatch);
  const missingRequiredResultIds = requiredResultIds.filter((mid) => !results[mid]);
  const hasResultsForRequiredBets = missingRequiredResultIds.length === 0;

  const canComputeDerived =
    !!me &&
    playerLoaded &&
    betsLoaded &&
    resultsLoaded &&
    matchesLoaded &&
    hasMatchesForBets &&
    hasResultsForRequiredBets;

  const derived = useMemo(() => {
    if (!me) return null;

    if (!canComputeDerived) {
      const cached = loadLastDerived(me.name);
      const base = cached ?? {
        earned: me.score || 0,
        won: me.wincount || 0,
        total: me.betcount || 0,
        winrate: me.betcount ? (me.wincount || 0) / me.betcount : 0,
        wallet: 0,
      };
      // Always recompute wallet from live me fields so sells/buys reflect immediately
      const wallet = STARTER_GOLD + (base.earned) + (me.match_gold || 0) - (me.spent || 0);
      return { ...base, wallet, stats: cStats(me), power: 0 };
    }
    return cDerived(me, bets, results, predScores, mById);
  }, [me, bets, results, predScores, mById, canComputeDerived]); // eslint-disable-line

  const TICKET_DROP_LEVELUP = me?.name === "aseo.bonjour" ? 0.30 : 0.10;
  const TICKET_DROP_BET = me?.name === "aseo.bonjour" ? 0.30 : 0.01;
  const TICKET_DROP_BET_EXACT = me?.name === "aseo.bonjour" ? 0.30 : 0.025;

  // ── Détection nouveaux résultats de paris ───────────────────────────────
  // Déclaré AVANT le level-up : dans un même commit, les résultats de paris
  // s'empilent en premier, donc s'affichent avant la pop-up de passage de niveau.
  useEffect(() => {
    if (!me || !derived) return;
    const seen = seenBetResultsRef.current;
    const fresh: BetResultData[] = [];
    for (const [mid, res] of Object.entries(results)) {
      const pick = bets[mid];
      if (!pick || seen.has(mid)) continue;
      const match = mById[mid];
      if (!match) continue;
      seen.add(mid);

      const isWin = res.o === pick;
      const pred = predScores[mid];
      const [ra, rb] = res.s ? res.s.split(/[–-]/).map(Number) : [NaN, NaN];
      const isExact = !!(pred && !isNaN(ra) && Number(pred.a) === ra && Number(pred.b) === rb);
      let pts = 0;
      let consolationPts: number | undefined;
      const m = mById[mid] as { p1: number; pX: number; p2: number; id: string } | undefined;
      if (m) {
        if (isWin) {
          pts = getBetPoints(m, pick);
          if (isExact) pts *= 2;
        } else {
          const minPts = Math.min(getBetPoints(m, "team1"), getBetPoints(m, "draw"), getBetPoints(m, "team2"));
          consolationPts = Math.round(minPts * 0.10);
        }
      }
      const dropRate = isExact ? TICKET_DROP_BET_EXACT : TICKET_DROP_BET;
      const gotTicket = isWin && Math.random() < dropRate;
      if (gotTicket) savePlayerTickets(me, 1);

      fresh.push({
        t1: (match as any).t1 ?? "",
        t2: (match as any).t2 ?? "",
        realScore: res.s ?? "",
        predictedScore: pred ? `${pred.a}-${pred.b}` : null,
        isWin,
        isExact,
        pointsEarned: pts,
        goldEarned: pts,
        gotTicket,
        consolationPts,
      });
    }
    if (fresh.length > 0) {
      sessionStorage.setItem("seen_bet_results", JSON.stringify([...seen]));
      setOverlayQueue((q) => [...q, { kind: "bet" as const, items: fresh }]);
    }
  }, [results]); // eslint-disable-line

  // Persiste le dernier derived fiable dans localStorage — utilisé comme fallback
  // pendant l'hydration pour éviter les valeurs fausses (0 pts / -320 gold).
  useEffect(() => {
    if (!canComputeDerived || !derived || !me) return;
    saveLastDerived(me.name, {
      earned: derived.earned,
      won: derived.won,
      total: derived.total,
      winrate: derived.winrate,
      wallet: derived.wallet,
    });
  }, [canComputeDerived, derived?.earned, derived?.wallet, me?.name]); // eslint-disable-line

  // ── Sync me.score/wallet avec derived (résultats de paris live) ──────────
  // me.score est aussi persisté en Supabase pour que le cold-start (après clear cache)
  // affiche directement la valeur correcte sans attendre le recalcul WC26/results.
  useEffect(() => {
    if (!me || !derived || !canComputeDerived) return;
    // Pas de sync si des résultats de matchs passés manquent encore.
    if (missingRequiredResultIds.length > 0) return;
    // Guard-rail : ne jamais écrire earned=0 si le score connu est positif et qu'on a des bets.
    if (derived.earned === 0 && (me.score || 0) > 0 && betIds.length > 0) {
      console.warn("[BLOCKED BAD SCORE SYNC]", {
        meScore: me.score,
        derivedEarned: derived.earned,
        resultsCount: Object.keys(results).length,
        missingRequiredResultIds,
      });
      return;
    }
    // Guard-rail : le nombre de paris résolus ne peut jamais diminuer. Un total plus
    // petit que me.betcount signale un chargement partiel ou croisé des bets (mauvais
    // player_name) — écrire écraserait score/wincount/betcount avec des données fausses.
    if (derived.total < (me.betcount || 0)) {
      console.warn("[BLOCKED SHRINKING BET SYNC]", {
        meBetcount: me.betcount,
        derivedTotal: derived.total,
        betsCount: betIds.length,
        resultsCount: Object.keys(results).length,
      });
      return;
    }
    if (
      me.score === derived.earned &&
      me.wincount === derived.won &&
      me.betcount === derived.total
    ) return;
    setMe((prev) => {
      if (!prev) return prev;
      const upd = { ...prev, score: derived.earned, wincount: derived.won, betcount: derived.total };
      saveCache(upd, bets);
      return upd;
    });
    setPlayerSource("sync-derived");
    // Persist score to Supabase — cold-starts lisent cette valeur avant que WC26/results chargent.
    sb.patch("players", `name=eq.${encodeURIComponent(me.name)}`, {
      score: derived.earned,
      wincount: derived.won,
      betcount: derived.total,
    });
    // Deps : inclut me.score/wincount/betcount pour re-synchroniser si me est écrasé
    // par une source périmée (ex: fetch player concurrent) alors que derived n'a pas bougé.
  }, [canComputeDerived, derived?.earned, derived?.won, derived?.total, me?.score, me?.wincount, me?.betcount]); // eslint-disable-line

  // Persist résultats dans localStorage à chaque mise à jour → disponibles dès le prochain refresh
  useEffect(() => {
    if (Object.keys(results).length > 0) saveResultsCache(results);
  }, [results]);

  // ── Détection level-up ──────────────────────────────────────────────────
  // Source de vérité récompenses : players.last_rewarded_level (Supabase).
  // La RPC claim_level_rewards distribue tickets + met à jour last_rewarded_level
  // dans une seule transaction → idempotent, multi-tab safe, retry safe.
  // localStorage seen_level_* : garde purement cosmétique pour supprimer la
  // popup au refresh ; n'a aucun rôle dans la décision de récompense.
  useEffect(() => {
    if (!derived || !me || !canComputeDerived) return;
    const level = playerLevel(derived.earned + (me.duel_xp ?? 0));
    const lastRewarded = me.last_rewarded_level ?? 1;

    if (level <= lastRewarded) {
      // Synchroniser localStorage (UI) si absent — premier chargement
      const uiKey = `seen_level_${me.name}`;
      if (!localStorage.getItem(uiKey)) localStorage.setItem(uiKey, String(level));
      return;
    }

    if (claimingLevelRef.current) return; // appel RPC déjà en vol
    claimingLevelRef.current = true;

    // Supprimer la popup au prochain refresh (cosmétique uniquement)
    localStorage.setItem(`seen_level_${me.name}`, String(level));

    sb.rpc("claim_level_rewards", { p_player_name: me.name, p_current_level: level })
      .then((result) => {
        claimingLevelRef.current = false;
        if (!result || result.error || !result.rewarded_levels?.length) return;
        setMe((prev) => prev ? {
          ...prev,
          last_rewarded_level: result.last_rewarded_level,
          tickets: (prev.tickets ?? 0) + result.tickets_granted,
        } : prev);
        enqueueOverlay({ kind: "levelup", level, gotTicket: result.tickets_granted > 0, vitalityGain: Math.max(1, level - lastRewarded) * VITALITY_PER_LEVEL });
      })
      .catch(() => { claimingLevelRef.current = false; });
  }, [derived?.earned, me?.name, me?.duel_xp, me?.last_rewarded_level]); // eslint-disable-line

  // Debug TopBar source — confirme que la TopBar lit bien me.score, pas derived.
  useEffect(() => {
    if (!me) return;
    const displayedPoints = me.score || 0;
    const displayedGold = STARTER_GOLD + (me.score || 0) + (me.match_gold || 0) - (me.spent || 0);
    console.log("[TOPBAR SOURCE]", {
      source: "player-row",
      playerSource,
      displayedPoints,
      displayedGold,
      meScore: me.score,
      spent: me.spent,
      matchGold: me.match_gold,
      derivedEarned: derived?.earned,
      derivedWallet: derived?.wallet,
      canComputeDerived,
      betsCount: Object.keys(bets).length,
      resultsCount: Object.keys(results).length,
      mByIdCount: Object.keys(mById).length,
      requiredResultIds: requiredResultIds.length,
      missingRequiredResultIds: missingRequiredResultIds.slice(0, 10),
      missingMatchIds: betIds.filter((mid) => !mById[mid]).slice(0, 10),
      missingResultIds: betIds.filter((mid) => !results[mid]).slice(0, 10),
    });
  }, [me?.score, me?.spent, me?.match_gold, playerSource, canComputeDerived]); // eslint-disable-line

  const savePlayerTickets = useCallback((player: Player, delta: number) => {
    setMe((prev) => {
      const base = prev ?? player;
      const upd = { ...base, tickets: (base.tickets ?? 0) + delta };
      saveCache(upd, bets);
      sb.patch("players", `name=eq.${encodeURIComponent(upd.name)}`, { tickets: upd.tickets });
      return upd;
    });
  }, [bets]);

  // Crédite réellement points (= bonus_points, qui alimente earned donc points ET gold)
  // et tickets sur le joueur, exactement comme un vrai résultat de paris.
  const creditRewards = useCallback((points: number, ticketsDelta: number) => {
    console.log("[CREDIT] called — points:", points, "tickets:", ticketsDelta);
    if (points === 0 && ticketsDelta === 0) { console.log("[CREDIT] skipped (both 0)"); return; }
    const prev = meRef.current;
    if (!prev) { console.log("[CREDIT] skipped — meRef.current is null"); return; }
    console.log("[CREDIT] prev bonus_points:", prev.bonus_points, "→ new:", (prev.bonus_points ?? 0) + points);
    const upd = {
      ...prev,
      bonus_points: (prev.bonus_points ?? 0) + points,
      tickets: (prev.tickets ?? 0) + ticketsDelta,
    };
    meRef.current = upd;
    setMe(upd);
    saveCache(upd, bets);
    console.log("[CREDIT] patching Supabase →", { bonus_points: upd.bonus_points, tickets: upd.tickets });
    sb.patch("players", `name=eq.${encodeURIComponent(upd.name)}`, {
      bonus_points: upd.bonus_points,
    }).then((ok) => {
      console.log("[CREDIT] patch bonus_points:", ok ? "✅ success" : "❌ FAILED");
    });
  }, [bets]);

  // Sync classement local quand bonus_points change (sandbox ou vraie récompense)
  // sans refetch Supabase — remplace juste l'entrée "me" et re-trie.
  React.useEffect(() => {
    if (!me || !derived) return;
    setPlayers((prev) => {
      if (!prev.length) return prev;
      const myIdx = prev.findIndex((x) => x.player.name === me.name);
      const entry = { player: me, derived };
      const next = myIdx >= 0
        ? prev.map((x, i) => (i === myIdx ? entry : x))
        : [...prev, entry];
      return [...next].sort((a, b) => b.derived.earned - a.derived.earned || b.derived.winrate - a.derived.winrate);
    });
  }, [me?.bonus_points]); // eslint-disable-line

  const handleTestLevelUp = useCallback(() => {
    if (!me) return;
    const level = playerLevel(derived?.earned ?? 0) + 1;
    const gotTicket = Math.random() < TICKET_DROP_LEVELUP;
    enqueueOverlay({ kind: "levelup", level, gotTicket });
    if (gotTicket) creditRewards(0, 1);
  }, [me, derived, TICKET_DROP_LEVELUP, creditRewards, enqueueOverlay]);

  const handleTestBetResult = useCallback(() => {
    if (!me) return;

    // Pioche un vrai pari + match si disponible, sinon fallback synthétique
    const betEntries = Object.entries(bets);
    const realEntry = betEntries.length > 0
      ? betEntries[Math.floor(Math.random() * betEntries.length)]
      : null;
    const realMatch = realEntry ? (mById[realEntry[0]] as any) : null;

    const t1: string = realMatch?.t1 ?? "France";
    const t2: string = realMatch?.t2 ?? "Argentina";
    const pick: string = realEntry?.[1] ?? "team1";
    const p1: number = realMatch?.p1 ?? 2.0;
    const pX: number = realMatch?.pX ?? 3.2;
    const p2: number = realMatch?.p2 ?? 3.5;

    // Même calcul que la vraie flow (App.tsx useEffect résultats)
    const roll = Math.random();
    const scenario = roll < 0.33 ? "loss" : roll < 0.66 ? "win" : "exact";
    const isWin = scenario !== "loss";
    const isExact = scenario === "exact";

    const mockMatch = { p1, pX, p2 };
    let pts = 0;
    let consolationPts: number | undefined;
    if (isWin) {
      pts = getBetPoints(mockMatch, pick);
      if (isExact) pts *= 2;
    } else {
      const minPts = Math.min(getBetPoints(mockMatch, "team1"), getBetPoints(mockMatch, "draw"), getBetPoints(mockMatch, "team2"));
      consolationPts = Math.round(minPts * 0.10);
    }

    const hi = 1 + Math.floor(Math.random() * 3);
    const lo = Math.floor(Math.random() * hi);
    const [a, b] = isWin ? [hi, lo] : [lo, hi];
    const predicted = isExact
      ? `${a}-${b}`
      : `${1 + Math.floor(Math.random() * 3)}-${Math.floor(Math.random() * 2)}`;

    const dropRate = isExact ? TICKET_DROP_BET_EXACT : TICKET_DROP_BET;
    const gotTicket = isWin && Math.random() < dropRate;
    // Victoire → pts, Défaite → consolationPts (sinon skippé à tort).
    creditRewards(pts + (consolationPts ?? 0), gotTicket ? 1 : 0);

    enqueueOverlay({
      kind: "bet",
      items: [{
        t1, t2,
        realScore: `${a}-${b}`,
        predictedScore: predicted,
        isWin, isExact,
        pointsEarned: pts, goldEarned: pts,
        gotTicket, consolationPts,
      }],
    });
  }, [me, bets, mById, TICKET_DROP_BET, TICKET_DROP_BET_EXACT, savePlayerTickets, creditRewards, enqueueOverlay]);

  const savePlayer = useCallback(
    async (upd: Player) => {
      setMe(upd);
      saveCache(upd, bets);

      const freshStats = cStats(upd);
      const calculatedPower =
        freshStats.technique +
        freshStats.defense +
        freshStats.physique +
        freshStats.vitesse;
      const finalPower = calculatedPower > 0 ? calculatedPower : 30;

      console.log(
        `[SAVE PLAYER] Joueur: ${upd.name} | Puissance Calculée: ${calculatedPower} | Envoyée: ${finalPower}`
      );

      // Cible par profile_id quand il existe (clé stable) — le nom peut entrer en
      // collision entre comptes (doublons de nicknames historiques en base).
      const filter = (upd as any).profile_id
        ? `profile_id=eq.${(upd as any).profile_id}`
        : `name=eq.${encodeURIComponent(upd.name)}`;
      await sb.patch("players", filter, {
        spent: upd.spent,
        match_gold: upd.match_gold || 0,
        inventory: upd.inventory,
        pending_items: upd.pending_items ?? [],
        coffre_items: upd.coffre_items ?? [],
        coffre_count: upd.coffre_count ?? 1,
        equipped: upd.equipped,
        avatar: upd.avatar,
        has_joined_league: upd.has_joined_league,
        power: Number(finalPower),
        auras: upd.auras ?? [],
        league_powers: upd.league_powers ?? [],
        match_powers: upd.match_powers ?? {},
        active_power: upd.active_power ?? null,
        league_elo: upd.league_elo ?? 1000,
        league_gold: upd.league_gold ?? STARTER_LEAGUE_GOLD,
        league_cards: upd.league_cards ?? null,
      });
    },
    [bets]
  );

  // Création joueur : profile_id OBLIGATOIRE pour tout vrai joueur.
  // Un player réel ne peut jamais être inséré sans profile_id.
  const createPlayer = useCallback(async (name: string, profileId: string, onDone?: (error?: string) => void): Promise<boolean> => {
    const trimmed = name.trim().slice(0, 18) || "Anonyme";
    const enc = encodeURIComponent(trimmed);
    saveName(trimmed);

    // ── 1. Lookup by profile_id (source de vérité) ────────────────────────────
    console.log("[GuestAuth] createPlayer — lookup by profile_id", { name: trimmed, profile_id: profileId });
    const existing = await sb.get(`players?profile_id=eq.${profileId}&limit=1`);
    console.log("[GuestAuth] createPlayer — profile_id result", {
      profile_id: profileId,
      found: existing[0] ? existing[0].name : "none",
    });
    if (existing[0]) {
      saveCache(existing[0], {});
      setMe(existing[0]);
      setBets({});
      onDone?.();
      return true;
    }

    // ── 2. Safety dedup by name (legacy rows or race condition) ───────────────
    const byName = await sb.get(`players?name=ilike.${enc}&limit=1`);
    console.log("[GuestAuth] createPlayer — name dedup check", {
      name: trimmed,
      found: byName[0] ? `yes (profile_id=${byName[0].profile_id ?? "null"})` : "none",
    });
    if (byName[0]) {
      if (!byName[0].profile_id) {
        // Legacy row without profile_id — stamp it and reuse
        console.log("[GuestAuth] createPlayer — stamping profile_id on legacy row", { name: trimmed, profileId });
        await sb.patch("players", `name=ilike.${enc}`, { profile_id: profileId });
        const stamped = { ...byName[0], profile_id: profileId };
        saveCache(stamped, {});
        setMe(stamped as any);
      } else {
        // Row exists with a DIFFERENT profile_id — c'est le compte d'un autre joueur.
        // Ne JAMAIS l'adopter : deux profils partageraient la même ligne players et
        // savePlayer (PATCH objet complet) écraserait les items/golds de l'autre.
        console.warn("[GuestAuth] createPlayer — name exists with DIFFERENT profile_id, rejecting nickname", {
          name: trimmed,
          existing_profile_id: byName[0].profile_id,
          requested_profile_id: profileId,
        });
        onDone?.("Nickname already taken");
        return false;
      }
      setBets({});
      onDone?.();
      return true;
    }

    // ── 3. New player — INSERT with profile_id always set ─────────────────────
    const starter = mkItem(rollRar(STD_ODDS));
    if (!starter) {
      console.error("[GuestAuth] createPlayer — starter item generation failed");
      onDone?.("Init error");
      return false;
    }
    const payload: Record<string, unknown> = {
      name: trimmed,
      profile_id: profileId,
      spent: 0,
      score: 0,
      wincount: 0,
      betcount: 0,
      inventory: [starter],
      equipped: { [starter.slot]: starter.iid },
    };
    console.log("[GuestAuth] create player payload", { name: payload.name, profile_id: payload.profile_id });
    const ok = await sb.insert("players", payload);
    if (!ok) {
      console.error("[GuestAuth] createPlayer — INSERT failed", { name: trimmed, profile_id: profileId });
      onDone?.("Failed to create player");
      return false;
    }
    const rows = await sb.get(`players?profile_id=eq.${profileId}&limit=1`);
    const player = rows[0] || payload;
    console.log("[GuestAuth] createPlayer — player ready", { name: player.name, profile_id: player.profile_id });
    saveCache(player, {});
    setMe(player);
    setBets({});
    setReveal({ items: [starter], kind: "std" });
    onDone?.();
    return true;
  }, []);

  // Login guest : device-bound via localStorage.
  //
  // Architecture:
  //   profiles.id          = stable PronoCup identity (stored in localStorage as guest_profile_id)
  //   profiles.auth_user_id = Supabase auth user — set later when guest links Google/email
  //   players.profile_id   = → profiles.id  (ALWAYS set for real players, never null)
  //   localStorage         = device-only shortcut, NOT the source of truth
  const handleGuest = useCallback(async (nickname: string, onDone: (error?: string) => void) => {
    console.log("[GuestAuth] handleGuest called", { nickname });
    const existingProfileId = getGuestProfileId();
    console.log("[GuestAuth] handleGuest — localStorage state", {
      guest_profile_id: existingProfileId ?? "null",
    });

    // ── CASE 1: returning guest — load by profile_id only, never by name ──────
    if (existingProfileId) {
      console.log("[GuestAuth] handleGuest — RETURNING GUEST", { profile_id: existingProfileId });
      setProfileId(existingProfileId);
      setIsGuestUser(true);
      const existing = await sb.get(`players?profile_id=eq.${existingProfileId}&limit=1`);
      console.log("[GuestAuth] handleGuest — player lookup by profile_id", {
        profile_id: existingProfileId,
        found: existing[0] ? existing[0].name : "none",
      });
      if (existing[0]) {
        saveName(existing[0].name as string);
        saveCache(existing[0], {});
        setMe(existing[0]);
        setBets({});
        onDone();
        return;
      }
      // Profile known but player row missing → create player (profile already exists)
      console.warn("[GuestAuth] handleGuest — player row missing for existing profile_id, creating player");
      await createPlayer(nickname, existingProfileId, onDone);
      return;
    }

    // ── CASE 2: new guest ─────────────────────────────────────────────────────
    // Check nickname availability against players.name (case-insensitive)
    const enc = encodeURIComponent(nickname);
    const taken = await sb.get(`players?name=ilike.${enc}&is_bot=eq.false&limit=1`);
    console.log("[GuestAuth] handleGuest — nickname availability check (players.name)", {
      nickname,
      taken: taken[0] ? `yes (profile_id=${taken[0].profile_id ?? "null"})` : "no",
    });
    if (taken[0]) {
      onDone("Nickname already taken");
      return;
    }

    // Create Supabase anonymous session (needed for RLS on profile insert)
    console.log("[GuestAuth] handleGuest — NEW GUEST, calling signInGuest()");
    let session;
    try {
      session = await signInGuest();
    } catch (e) {
      console.error("[GuestAuth] handleGuest — signInGuest error:", e);
    }
    if (!session) {
      // No session = cannot create profile safely. Hard stop, no fallback without profile_id.
      console.error("[GuestAuth] handleGuest — no session, aborting (no profile_id = no insert)");
      onDone("Connection failed, please try again");
      return;
    }
    console.log("[GuestAuth] handleGuest — signInGuest ok", { user_id: session.user.id });

    // Create profile row
    const profile = await ensureProfile(session, nickname, true);
    console.log("[GuestAuth] handleGuest — ensureProfile result", {
      profile_id: profile?.id ?? "null",
      nickname: profile?.nickname ?? "null",
    });
    if (!profile?.id) {
      console.error("[GuestAuth] handleGuest — profile creation failed, aborting");
      onDone("Account creation failed, please try again");
      return;
    }

    // Store profile_id BEFORE creating player (so cleanup is possible on failure)
    setGuestProfileId(profile.id);
    setProfileId(profile.id);
    setIsGuestUser(true);

    // Create player row — profile_id always set
    const ok = await createPlayer(nickname, profile.id, onDone);
    if (!ok) {
      // Player creation failed — delete the profile to avoid orphan row
      console.error("[GuestAuth] handleGuest — player creation failed, cleaning up profile", { profile_id: profile.id });
      await sb.delete("profiles", `id=eq.${profile.id}`);
      clearGuestProfileId();
    }
  }, [createPlayer]);

  // Login OAuth : on stocke le pseudo (s'il existe) puis on redirige. Au retour,
  // le boot parse le hash OAuth, applique pending_nickname au profil, charge le joueur.
  const handleOAuth = useCallback((provider: Provider, nickname: string) => {
    if (nickname) setPendingNickname(nickname);
    signInWithProvider(provider); // navigates away — no return value
  }, []);

  // Sign-out : efface la session auth + cache local puis recharge la page (→ Login).
  const handleSignOut = useCallback(() => {
    authSignOut();
    clearGuestProfileId();
    localStorage.removeItem("pronocup_v4_name");
    localStorage.removeItem("pronocup_v4_player");
    localStorage.removeItem("pronocup_v4_bets");
    window.location.reload();
  }, []);

  // Link a social identity to the current guest account.
  // Uses the dedicated identity-link endpoint (not a plain sign-in) so Supabase
  // attaches Google to the existing guest user rather than creating/switching accounts.
  // No fallback to signInWithProvider — any failure surfaces as an error to the user.
  const handleLinkAccount = useCallback(async (provider: "google") => {
    const session = getSession();
    if (!session?.access_token) {
      setOauthError("You must be signed in to link an account.");
      return;
    }
    // Do NOT store pendingNickname here — linkIdentityWithProvider handles sessionStorage internally
    try {
      await linkIdentityWithProvider(provider as Provider, session.access_token, session.user.id);
      // If we reach here the page has NOT navigated yet (error thrown before window.location.href)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Could not link Google account. Please try again.";
      console.error("[Auth] handleLinkAccount error:", e);
      setOauthError(msg);
    }
  }, []);


  // Post-OAuth nickname : crée le profil avec le pseudo choisi puis le joueur de départ.
  const handleNicknameChosen = useCallback(async (nickname: string): Promise<string | void> => {
    const session = getSession();
    if (!session) return "Not signed in — please reload the page";
    try {
      // Disponibilité AVANT de toucher au profil : sinon on renommerait le profil
      // vers un pseudo appartenant déjà à un autre joueur (collision players.name).
      const enc = encodeURIComponent(nickname.trim());
      const taken = await sb.get(`players?name=ilike.${enc}&is_bot=eq.false&limit=1`);
      if (taken[0] && taken[0].profile_id !== session.user.id)
        return "Nickname already taken";
      // allowRename=true : choix explicite de l'utilisateur (seul endroit autorisé).
      const profile = await ensureProfile(session, nickname, false, true);
      if (!profile?.id || !profile?.nickname)
        return "Account setup failed, please try again";
      saveName(profile.nickname);
      const ok = await createPlayer(profile.nickname, profile.id);
      if (!ok) return "Nickname already taken";
      setNeedsNickname(false);
    } catch (e) {
      console.warn("nickname setup:", e);
      return "Something went wrong, please try again";
    }
  }, [createPlayer]);

  // ── Récupération d'identité — reconnecte un player existant au user Google ────
  // Automatise le fix SQL manuel des incidents SJ/camille/Luka (07/2026) :
  // profil sur le user courant (nickname = players.name, jamais l'inverse — les
  // bets sont clés par player_name), puis re-pointage de players.profile_id.
  const handleRecoverAccount = useCallback(async (): Promise<string | void> => {
    const session = getSession();
    const name = recoverName;
    if (!session || !name) return "Not signed in — please reload the page";
    try {
      const enc = encodeURIComponent(name);
      const rows = await sb.get(`players?name=eq.${enc}&is_bot=eq.false&limit=1`);
      const player = rows[0];
      if (!player) return "Could not connect the account, please try again";
      // Le user Google a-t-il DÉJÀ un player ? (ex: compte vide créé lors d'un
      // passage précédent par NicknameSetup). Le re-pointage violerait l'index
      // unique players_profile_id_unique_real_users → fusion manuelle uniquement.
      const mine = await sb.get(`players?profile_id=eq.${session.user.id}&limit=1`);
      if (mine[0] && mine[0].name !== player.name) {
        console.error("[Auth] recover — google user already owns a different player", {
          owned: mine[0].name,
          requested: player.name,
        });
        return "This Google account already has a player. Please contact support.";
      }
      // 1. Profil d'abord (FK players.profile_id → profiles.id).
      const profile = await ensureProfile(session, player.name, false, true);
      if (!profile?.id) return "Could not connect the account, please try again";
      if (profile.nickname !== player.name) {
        const renamed = await updateProfileNickname(session, player.name);
        if (!renamed) return "Could not connect the account, please try again";
      }
      // 2. Re-pointage du player vers le user courant — via le RPC vérifié
      //    recover_player (SECURITY DEFINER). Le patch direct est désormais BLOQUÉ
      //    par le trigger players_guard_profile_id : profile_id est immuable sauf
      //    passage par ce RPC (empêche le fork silencieux — incidents identité 07/2026).
      const res = await sb.rpcAuthed("recover_player", { target_name: player.name }, session.access_token);
      if (!res.ok || !res.data) {
        console.error("[Auth] recover RPC failed:", res.error);
        if (res.error && /already owns/i.test(res.error))
          return "This Google account already has a player. Please contact support.";
        if (res.error && /anonymous/i.test(res.error))
          return "Sign in with Google before recovering your account.";
        return "Could not connect the account, please try again";
      }
      console.log("[Auth] recover SUCCESS — player reconnected", { name: player.name, profile_id: session.user.id });
      saveName(player.name);
      clearGuestProfileId();
      clearSessionBackup();
      // Reboot propre : le boot recharge player + bets par profile_id.
      window.location.reload();
    } catch (e) {
      console.error("[Auth] recover error:", e);
      return "Could not connect the account, please try again";
    }
  }, [recoverName]);

  const handleDeclineRecovery = useCallback(() => {
    // L'utilisateur affirme que ce n'est pas son compte : purger le nom local
    // (sinon le prochain boot re-proposerait la récupération du même player).
    localStorage.removeItem("pronocup_v4_name");
    setRecoverName(null);
    setNeedsNickname(true);
  }, []);

  const handleRestoreBackupSession = useCallback(() => {
    if (restoreSessionBackup()) window.location.reload();
  }, []);

  const loadPlayers = useCallback(async () => {
    // Lecture des refs — stables, jamais stale, ne recrée pas le callback
    const curMe = meRef.current;
    const curBets = betsRef.current;
    const curPreds = predScoresRef.current;
    const curMById = mByIdRef.current;
    const curResults = resultsRef.current;

    // Une seule requête, colonnes strictement nécessaires à la liste.
    // score/wincount/betcount/power sont déjà dénormalisés en base (écrits au fil des paris
    // et des changements d'équipement) : plus besoin de retélécharger tous les `bets` ni
    // l'`inventory` de chaque joueur — l'inventaire est chargé à la volée dans la modale.
    const rawP = await sb.get(
      "players?select=name,avatar,score,wincount,betcount,power,bonus_points,duel_reputation,duel_rank,duel_mmr,duel_xp,pve_current_tier,pve_best_tier,pve_best_stage&is_bot=eq.false&order=score.desc"
    );

    let arr = rawP.map((p) => ({
      player: p,
      derived: {
        earned: p.score ?? 0,
        won: p.wincount ?? 0,
        total: p.betcount ?? 0,
        winrate: p.betcount ? (p.wincount ?? 0) / p.betcount : 0,
        power: p.power ?? 0,
        stats: {} as Record<string, number>, // rempli à la volée à l'inspection
      },
    }));

    // Pour mon propre joueur : on injecte le derived live (déjà calculé en mémoire) pour éviter
    // tout décalage avec la TopBar — la DB peut être en retard de quelques secondes.
    if (curMe) {
      const myIdx = arr.findIndex((x) => x.player.name === curMe.name);
      const myDerived = cDerived(curMe, curBets, curResults, curPreds, curMById);
      if (myIdx >= 0) arr[myIdx] = { player: curMe, derived: myDerived };
      else arr.push({ player: curMe, derived: myDerived });
    }

    arr.sort((a, b) => b.derived.earned - a.derived.earned || b.derived.winrate - a.derived.winrate);
    setPlayers(arr);
    setPlayersLoaded(true);

  }, []); // eslint-disable-line

  // Inspection d'un joueur : la liste ne charge plus l'inventaire de tout le monde.
  // On récupère à la volée l'inventaire/équipement du seul joueur cliqué, puis on
  // calcule ses stats détaillées pour la modale.
  const inspectPlayer = useCallback(async (row: PlayerRow) => {
    const p = row.player;
    const token = ++inspectReq.current;
    // Ouverture instantanée avec ce qu'on a déjà en liste — jamais d'attente réseau bloquante.
    setInspecting(row);
    // Mon propre profil : inventaire + stats détaillées déjà en mémoire, rien à charger.
    if (p.name === meRef.current?.name) {
      setInspectLoading(false);
      return;
    }

    const applyFull = (full: any) => {
      const fullPlayer = { ...p, ...full };
      const stats = cStats(fullPlayer);
      setInspecting({
        player: fullPlayer,
        derived: {
          ...row.derived,
          stats,
          power: stats.technique + stats.defense + stats.physique + stats.vitesse,
        },
      });
    };

    // Déjà consulté cette session → affichage instantané depuis le cache, aucun fetch.
    const cached = inspectCacheRef.current[p.name];
    if (cached) {
      applyFull(cached);
      setInspectLoading(false);
      return;
    }

    // Sinon on complète l'inventaire/l'équipement/les stats du joueur en arrière-plan (skeleton).
    setInspectLoading(true);
    const startedAt = Date.now();
    const rows = await sb.get(
      `players?name=eq.${encodeURIComponent(p.name)}&select=inventory,equipped,bonus_points,match_gold,spent&limit=1`
    );
    // Requête périmée (modale fermée ou autre joueur cliqué entre-temps) → on ignore.
    if (inspectReq.current !== token) return;
    const full = rows[0] || {};
    inspectCacheRef.current[p.name] = full;
    // Durée minimale d'affichage du skeleton pour éviter un flash imperceptible sur fetch rapide.
    const MIN_SKELETON = 450;
    const remaining = MIN_SKELETON - (Date.now() - startedAt);
    if (remaining > 0) await new Promise((r) => setTimeout(r, remaining));
    if (inspectReq.current !== token) return;
    applyFull(full);
    setInspectLoading(false);
  }, []);

  useEffect(() => {
    // Lobby (tab par défaut) affiche la position leaderboard Duel → il a besoin de
    // players, au même titre que le Classement.
    if (tab === "classement" || tab === "lobby") loadPlayers();
  }, [tab, loadPlayers]);

  // Mise à jour live de ma propre ligne dans le classement quand derived change —
  // sans refetch Supabase, pour que TopBar et Classement soient toujours synchrones.
  useEffect(() => {
    if (!me || !derived || !players.length) return;
    setPlayers((prev) => {
      const idx = prev.findIndex((x) => x.player.name === me.name);
      if (idx < 0) return prev;
      const next = [...prev];
      next[idx] = { player: me, derived };
      next.sort((a, b) => b.derived.earned - a.derived.earned || b.derived.winrate - a.derived.winrate);
      return next;
    });
  }, [derived?.earned, derived?.won, derived?.total, me?.duel_xp, me?.duel_reputation]); // eslint-disable-line

  // Paris : UPSERT immédiat avec blaze + pick + scores prédits
  const placeBet = useCallback(
    async (mid: string, pick: string, scoreA: number | null = null, scoreB: number | null = null) => {
      if (isLocked(mById[mid] ?? M_BY_ID[mid], results)) return;
      const nextBets = { ...bets, [mid]: pick };
      setBets(nextBets);
      saveCache(me, nextBets);
      const row: Record<string, any> = { player_name: me!.name, match_id: mid, pick };
      if (scoreA !== null) row.score_a = scoreA;
      if (scoreB !== null) row.score_b = scoreB;
      await sb.upsert("bets", row);
    },
    [me, bets, results]
  );

  const setResult = useCallback(
    async (mid: string, outcome: string, score: string) => {
      setResults((prev) => ({ ...prev, [mid]: { o: outcome, s: score } }));
      await sb.upsert("match_results", { match_id: mid, outcome, score });
      flash("Score saved for the whole team 🙌");
    },
    [flash]
  );
  const equipItem = React.useCallback(
    async (it: Item) => {
      if (!me) return;
      const newEquipped: Record<string, string | null> = { ...(me.equipped || {}), [it.slot]: it.iid };
      const upd: Player = { ...me, equipped: newEquipped };
      await savePlayer(upd);
    },
    [me, savePlayer]
  );
  const unequipItem = React.useCallback(
    async (it: Item) => {
      if (!me) return;
      const newEquipped: Record<string, string | null> = { ...(me.equipped || {}) };
      newEquipped[it.slot] = null;
      const upd: Player = { ...me, equipped: newEquipped };
      await savePlayer(upd);
    },
    [me, savePlayer]
  );
  const sellItem = useCallback(
    async (it: Item) => {
      if (!me) return;
      const newEquipped: Record<string, string | null> = { ...(me.equipped || {}) };
      if (newEquipped[it.slot] === it.iid) newEquipped[it.slot] = null;
      const newInventory = (me.inventory || []).filter((x) => x.iid !== it.iid);
      const gain = SELL_PRICE[it.rarity] || 20;

      // Une place vient de se libérer → on fait glisser le 1er reward en attente
      // (FIFO) dans l'inventaire, dans le MÊME save (atomique côté client).
      const pending = me.pending_items ?? [];
      const claimed = pending.length > 0 ? pending[0] : null;
      const nextInventory = claimed ? [...newInventory, claimed] : newInventory;

      const upd: Player = {
        ...me,
        equipped: newEquipped,
        inventory: nextInventory,
        pending_items: claimed ? pending.slice(1) : pending,
        spent: (me.spent || 0) - gain,
      };
      await savePlayer(upd);
      flash(`+${gain} coins 💰`);
      if (claimed) setReveal({ items: [claimed], kind: "premium" });
    },
    [me, savePlayer, flash]
  );

  // Coffre (rangement manuel) : déposer un item de l'inventaire vers le coffre.
  // Ranger libère un slot d'inventaire → on draine 1 pending (même invariant que
  // sellItem : pending>0 ⇒ inventaire plein). L'item déposé n'est jamais équipé
  // (décision "ressortir d'abord"), donc pas de equipped à nettoyer.
  const storeInCoffre = useCallback(
    async (it: Item) => {
      if (!me) return;
      if (coffreIsFull(me.coffre_items, me.coffre_count)) {
        flash("Vault is full — buy another chest or retrieve items");
        return;
      }
      const newInventory = (me.inventory || []).filter((x) => x.iid !== it.iid);
      const newEquipped: Record<string, string | null> = { ...(me.equipped || {}) };
      if (newEquipped[it.slot] === it.iid) newEquipped[it.slot] = null; // filet de sécurité
      const pending = me.pending_items ?? [];
      const claimed = pending.length > 0 ? pending[0] : null;
      const nextInventory = claimed ? [...newInventory, claimed] : newInventory;
      await savePlayer({
        ...me,
        inventory: nextInventory,
        equipped: newEquipped,
        coffre_items: [...(me.coffre_items ?? []), it],
        pending_items: claimed ? pending.slice(1) : pending,
      });
      flash("Stored in vault 📦");
      if (claimed) setReveal({ items: [claimed], kind: "premium" });
    },
    [me, savePlayer, flash]
  );

  // Coffre : ressortir un item vers l'inventaire (bloqué si l'inventaire est plein).
  const retrieveFromCoffre = useCallback(
    async (it: Item) => {
      if (!me) return;
      if (inventoryIsFull(me.inventory, me.equipped)) {
        flash("Inventory full — sell, store, or equip an item first");
        return;
      }
      await savePlayer({
        ...me,
        inventory: [...(me.inventory || []), it],
        coffre_items: (me.coffre_items ?? []).filter((x) => x.iid !== it.iid),
      });
      flash("Retrieved from vault ⬅️");
    },
    [me, savePlayer, flash]
  );

  // Coffre : acheter un coffre supplémentaire (+20 slots) contre 15 tickets.
  const buyCoffreChest = useCallback(async () => {
    if (!me) return;
    if ((me.coffre_count ?? 1) >= MAX_COFFRE_CHESTS) {
      flash("Vault already at maximum size");
      return;
    }
    if ((me.tickets ?? 0) < COFFRE_CHEST_COST_TICKETS) {
      flash(`Need ${COFFRE_CHEST_COST_TICKETS} tickets for a new chest`);
      return;
    }
    await savePlayer({
      ...me,
      tickets: (me.tickets ?? 0) - COFFRE_CHEST_COST_TICKETS,
      coffre_count: (me.coffre_count ?? 1) + 1,
    });
    flash("New vault chest unlocked 📦 +20 slots");
  }, [me, savePlayer, flash]);
  const buyPack = useCallback(
    async (kind: string, opts?: { free?: boolean }) => {
      const free = opts?.free ?? false;
      if (isPurchasing.current) return;
      isPurchasing.current = true;
      try {
        let cost = PACK_STD;
        let odds = STD_ODDS;

        if (kind === "premium") {
          cost = PACK_PREM;
          odds = PREM_ODDS;
        } else if (kind === "familier") {
          cost = PACK_FAM;
          odds = FAM_ODDS;
        }

        if (!me || !derived || (!free && derived.wallet < cost)) {
          flash("Not enough coins — win some predictions!");
          return;
        }

        // Achat PAYÉ inventaire plein : on bloque AVANT de débiter (le joueur a
        // l'agency — qu'il vende d'abord). Le claim GRATUIT, lui, a déjà déclenché
        // markClaimed côté UI : le bloquer perdrait la récompense → il est routé
        // vers pending_items plus bas (non destructif).
        if (!free && inventoryIsFull(me.inventory, me.equipped)) {
          flash("Inventory full — sell, store, or equip an item to free a slot");
          return;
        }

        let items: any[];
        if (kind === "familier") {
          const rolled = rollRar(odds);
          // Reroll to a valid rarity if familiar has no items for the rolled one
          const validFamRars = Object.entries(INAMES.familiar)
            .filter(([, arr]) => arr.length > 0)
            .map(([r]) => r);
          const famRar = (INAMES.familiar[rolled]?.length ?? 0) > 0
            ? rolled
            : validFamRars[Math.floor(Math.random() * validFamRars.length)];
          if (!famRar) { console.error("[buyPack] No familiars defined in INAMES.familiar"); return; }
          const famPool = INAMES.familiar[famRar]!;
          const famName = famPool[Math.floor(Math.random() * famPool.length)];
          items = [{ iid: "i_" + Date.now() + "_" + Math.random().toString(36).substr(2, 5), slot: "familiar", rarity: famRar, name: famName }];
        } else {
          const lvl = playerLevel(derived?.earned || 0);
          const item = mkItem(rollRar(odds), lvl);
          if (!item) { console.error("[buyPack] mkItem returned null"); return; }
          items = [item];
        }

        console.log("[openPack] items generated", items.map((it: any) => ({ name: it.name, slot: it.slot, rarity: it.rarity, iid: it.iid })));

        // Seul le claim GRATUIT peut atteindre ce point avec l'inventaire plein (le
        // payé a été bloqué plus haut). On parke dans pending_items ce qui rentre,
        // le reste est auto-converti en coins — jamais de perte.
        if (inventoryIsFull(me.inventory, me.equipped)) {
          const pending = me.pending_items ?? [];
          const room = Math.max(0, PENDING_CAP - pending.length);
          const toPark = items.slice(0, room);
          const toCoins = items.slice(room);
          const coinGain = toCoins.reduce((s, it) => s + (SELL_PRICE[it.rarity] || 20), 0);
          await savePlayer({
            ...me,
            pending_items: [...pending, ...toPark],
            spent: (me.spent || 0) - coinGain,
          });
          console.log("[openPack] inventory full → parked/converted", { parked: toPark.length, coinGain });
          if (toPark.length > 0) setPendingModal(true);
          if (coinGain > 0) flash(`Inventory full — +${coinGain} coins 💰`);
          return;
        }

        const upd: Player = {
          ...me,
          spent: (me.spent || 0) + (free ? 0 : cost),
          inventory: [...(me.inventory || []), ...items],
        };

        await savePlayer(upd);
        console.log("[openPack] savePlayer success");
        setReveal({ items, kind });
      } finally {
        isPurchasing.current = false;
      }
    },
    [me, derived, savePlayer, flash, mkItem]
  );

  // `extra` : champs fusionnés dans le savePlayer du grant (ex. duel_chest_claimed).
  // Nécessaire car setMe(...) juste avant l'appel n'est pas encore reflété dans la
  // closure `me` : sans ce merge, le savePlayer réécrit l'ancienne valeur du flag
  // et le coffre redevient réclamable (bug coffre infini).
  const openFreeChestPack = useCallback(async (extra?: Partial<Player>) => {
    if (!me) return;
    const base = { ...me, ...extra };
    const lvl = playerLevel(derived?.earned || 0);
    const item = mkItem(rollRar(PREM_ODDS), lvl);
    if (!item) { console.error("[openFreeChestPack] mkItem returned null"); return; }
    console.log("[openFreeChestPack] item generated", { name: item.name, slot: item.slot, rarity: item.rarity, iid: item.iid });

    // Inventaire plein : le gain ne peut pas entrer. On le parke dans pending_items
    // (mailbox) jusqu'à ce qu'une place se libère (voir sellItem). Au-delà de
    // PENDING_CAP, on auto-convertit en coins pour ne JAMAIS perdre la récompense.
    // Lecture de l'état frais `me` (pas une closure périmée) au moment du grant.
    if (inventoryIsFull(me.inventory, me.equipped)) {
      const pending = me.pending_items ?? [];
      if (pending.length < PENDING_CAP) {
        await savePlayer({ ...base, pending_items: [...pending, item] });
        console.log("[openFreeChestPack] inventory full → parked in pending_items");
        setPendingModal(true);
      } else {
        // File pleine : conversion en coins via le même canal que la vente (spent -=).
        const gain = SELL_PRICE[item.rarity] || 20;
        await savePlayer({ ...base, spent: (me.spent || 0) - gain });
        console.log("[openFreeChestPack] inventory & pending full → converted to coins", { gain });
        flash(`Inventory full — reward converted to +${gain} coins 💰`);
      }
      return;
    }

    await savePlayer({ ...base, inventory: [...(me.inventory || []), item] });
    console.log("[openFreeChestPack] savePlayer success");
    setReveal({ items: [item], kind: "premium" });
  }, [me, derived, savePlayer, mkItem, flash]);

  const equip = useCallback(
    async (it: Item) => {
      if (!me) return;
      const eq: Record<string, string | null> = { ...(me.equipped || {}) };
      if (eq[it.slot] === it.iid) eq[it.slot] = null;
      else eq[it.slot] = it.iid;
      await savePlayer({ ...me, equipped: eq });
    },
    [me, savePlayer]
  );

  const autoEquip = useCallback(async () => {
    if (!me) return;
    const best: Record<string, Item> = {};
    (me.inventory || []).forEach((it) => {
      if (!best[it.slot] || iPower(it) > iPower(best[it.slot]))
        best[it.slot] = it;
    });
    const eq: Record<string, string | null> = {};
    Object.values(best).forEach((it) => {
      eq[it.slot] = it.iid;
    });
    await savePlayer({ ...me, equipped: eq });
    flash("Best gear equipped ⚡");
  }, [me, savePlayer, flash]);

  const earnCoins = useCallback(
    async (amount: number) => {
      if (!me) return;
      const upd: Player = { ...me, match_gold: (me.match_gold || 0) + amount };
      await savePlayer(upd);
      flash(`+${amount} coins earned! 🏆`);
    },
    [me, savePlayer, flash]
  );

  const earnCup = useCallback(async () => {
    if (!me) return;
    const upd: Player = { ...me, cups: (me.cups || 0) + 1 };
    await savePlayer(upd);
    flash("K League trophy won! 🏆");
  }, [me, savePlayer, flash]);

  // Rappel persistant (mailbox) : une fois par chargement d'app, si une récompense
  // attend ET que l'inventaire est encore plein, on ré-invite à libérer une place.
  // Le ref garantit qu'on ne re-nag PAS à chaque mutation (vente, équipement) — un
  // reload repart d'un ref neuf → le rappel réapparaît, comme voulu.
  React.useEffect(() => {
    if (boot || !me || pendingNaggedRef.current) return;
    if ((me.pending_items?.length ?? 0) > 0 && inventoryIsFull(me.inventory, me.equipped)) {
      setPendingModal(true);
      pendingNaggedRef.current = true;
    }
  }, [boot, me]);

  const { ready: imagesReady, progress: imgProgress } = useImagePreloader();

  if (boot || !imagesReady)
    return (
      <Shell>
        <Style />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100dvh",
            gap: 24,
            padding: 32,
          }}
        >
          {/* Logo / nom */}
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>⚽</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: C.text, letterSpacing: -0.5 }}>
              Prono<span style={{ color: C.turf }}>Cup</span>
            </div>
          </div>

          {/* Barre de progression */}
          <div style={{ width: "100%", maxWidth: 200 }}>
            <div
              style={{
                height: 4,
                borderRadius: 999,
                background: "rgba(255,255,255,0.08)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  borderRadius: 999,
                  background: C.turf,
                  width: `${Math.round(imgProgress * 100)}%`,
                  transition: "width 0.15s ease",
                }}
              />
            </div>
          </div>
        </div>
      </Shell>
    );
  if (!me && recoverName)
    return (
      <Shell>
        <Style />
        <RecoverAccount
          name={recoverName}
          hasBackup={(() => {
            const b = loadSessionBackup();
            return !!b && b.user.id !== getSession()?.user.id;
          })()}
          onRecover={handleRecoverAccount}
          onDecline={handleDeclineRecovery}
          onRestoreBackup={handleRestoreBackupSession}
        />
      </Shell>
    );
  if (!me && needsNickname)
    return (
      <Shell>
        <Style />
        <NicknameSetup onSubmit={handleNicknameChosen} />
      </Shell>
    );
  if (!me)
    return (
      <Shell>
        <Style />
        <Login onGuest={handleGuest} onOAuth={handleOAuth} />
      </Shell>
    );

  return (
    <Shell>
      <Style />
      {/* Barre du haut globale = AppTopBar du Lobby (niveau/XP + coins + tickets +
          feedback). TOUJOURS montée (on togglé juste `display`, pas de démontage
          conditionnel) → les <img> coins/tickets ne se rechargent pas au changement
          de page. Cachée sur le Lobby, qui embarque déjà sa propre barre dans le
          décor. Même padding-top que le Lobby (safe-area + 12) → position identique. */}
      <header
        style={{
          display: tab === "lobby" ? "none" : "block",
          position: "sticky", top: 0, zIndex: 40,
          padding: "calc(env(safe-area-inset-top, 0px) + 12px) 14px 10px",
        }}
      >
        <AppTopBar
          xp={levelProgress((derived?.earned ?? 0) + (me.duel_xp ?? 0))}
          gold={
            derived?.wallet ??
            STARTER_GOLD +
              (me.score || 0) +
              (me.match_gold || 0) -
              (me.spent || 0)
          }
          tickets={me?.tickets ?? 0}
          onOpenShop={() => { setTab("packs"); window.scrollTo(0, 0); }}
          onFeedback={() => setFeedbackOpen(true)}
        />
      </header>
      <main>
        {tab === "pronos" && (
          <Pronos
            me={me}
            bets={bets}
            results={results}
            onBet={placeBet}
            onSetResult={setResult}
            refScores={refScores}
            onRefScore={updateRefScore}
            predScores={predScores}
            onPredScore={updatePredScore}
            matches={matches}
          />
        )}
        {/* Lobby (maquette test) : gardé monté via CSS display comme les autres tabs */}
        <div style={{ display: tab === "lobby" ? "block" : "none" }}>
          {me && derived && (
            <Lobby
              me={me}
              earned={derived.earned}
              gold={derived.wallet}
              players={players}
              playersLoaded={playersLoaded}
              onPlayTrial={() => { setLeagueEntryView("trial"); setTab("league"); window.scrollTo(0, 0); }}
              onPlayDuel={() => { setLeagueEntryView("duel"); setTab("league"); window.scrollTo(0, 0); }}
              onOpenLeaderboard={() => { setClassementFocus("duel"); setTab("classement"); window.scrollTo(0, 0); }}
              onOpenShop={() => { setTab("packs"); window.scrollTo(0, 0); }}
              onFeedback={() => setFeedbackOpen(true)}
              onChestClaimed={() => {
                // Le flag est fusionné dans le save du grant (openFreeChestPack) pour
                // éviter le clobber par la closure `me` périmée → coffre non-infini.
                openFreeChestPack({ duel_chest_claimed: true });
              }}
            />
          )}
        </div>
        {/* Classement/Packs : gardés montés via CSS display pour éviter le reflush d'images */}
        <div style={{ display: tab === "classement" ? "block" : "none" }}>
          <Pad>
            <Classement
              players={players}
              myName={me.name}
              loading={!playersLoaded}
              onInspect={inspectPlayer}
              focusTab={classementFocus}
              onFocusConsumed={() => setClassementFocus(null)}
            />
          </Pad>
        </div>
        <div style={{ display: tab === "packs" ? "block" : "none" }}>
          <Pad>
            <Packs
              me={me}
              derived={derived!}
              onBuy={buyPack}
              onClaimFree={(kind: string) => buyPack(kind, { free: true })}
              inventoryFull={inventoryIsFull(me?.inventory, me?.equipped)}
              tickets={me?.tickets ?? 0}
              level={playerLevel(derived?.earned ?? 0)}
              onBuyCosmeticPack={() => {
                if (!me || (me.tickets ?? 0) < 15) return;
                const upd = { ...me, tickets: (me.tickets ?? 0) - 15 };
                savePlayer(upd);
                flash("Pack cosmétique ouvert ! 🎨");
              }}
              isAdmin={me?.name === "aseo.bonjour"}
              onAdminDrop={(item) => {
                if (!me) return;
                savePlayer({ ...me, inventory: [...(me.inventory ?? []), item] });
                flash(`✅ ${item.name} ajouté à l'inventaire`);
              }}
            />
          </Pad>
        </div>
        {tab === "match" && (
          <Pad>
            <TestMatch me={me} onEarnCoins={earnCoins} onEarnCup={earnCup} />
          </Pad>
        )}
        {/* League : gardé monté via CSS display pour éviter le reset des animations donut */}
        <div style={{ display: tab === "league" ? "block" : "none" }}>
          <LeagueWidget
            player={me}
            savePlayer={savePlayer}
            forcedView={leagueEntryView}
            onForcedViewConsumed={() => setLeagueEntryView(null)}
            onExitToLobby={() => { setTab("lobby"); window.scrollTo(0, 0); }}
            onDuelXpUpdate={(xpAfter) => setMe((prev) => prev ? { ...prev, duel_xp: xpAfter } : prev)}
            onDuelGoldEarned={(nextMatchGold) => setMe((prev) => prev ? { ...prev, match_gold: nextMatchGold } : prev)}
            onDuelRankUpdate={(patch) => setMe((prev) => prev ? { ...prev, duel_reputation: patch.reputation, duel_mmr: patch.mmr, duel_rank: patch.rank } : prev)}
            onPveComplete={(patch) => setMe((prev) => prev ? {
              ...prev,
              match_gold: (prev.match_gold ?? 0) + patch.match_gold_delta,
              // XP PVE = duel_xp (valeur absolue confirmée en DB, comme onDuelXpUpdate)
              duel_xp: patch.duel_xp_after ?? prev.duel_xp,
              pve_condition: patch.pve_condition,
              pve_best_stage: patch.pve_best_stage,
              pve_trial_chests: (prev.pve_trial_chests ?? 0) + patch.pve_trial_chests_delta,
            } : prev)}
            wallet={derived?.wallet ?? 0}
            bettingPoints={derived?.earned ?? 0}
            onToast={flash}
            isTester={me?.name === "aseo.bonjour"}
            onOpenPremiumPack={openFreeChestPack}
          />
        </div>
        {/* Profil : gardé monté via CSS display pour éviter le reflush d'images au retour */}
        <div style={{ display: tab === "profil" ? "block" : "none" }}>
          <Pad>
            <Profil
              me={me}
              derived={derived!}
              bets={bets}
              results={results}
              onEquip={equipItem}
              onUnequip={unequipItem}
              onAuto={autoEquip}
              savePlayer={savePlayer}
              onSell={sellItem}
              onStore={storeInCoffre}
              onRetrieve={retrieveFromCoffre}
              onBuyCoffre={buyCoffreChest}
              onTestLevelUp={handleTestLevelUp}
              onTestBetResult={handleTestBetResult}
              onSignOut={handleSignOut}
              isGuest={isGuestUser}
              onLinkAccount={handleLinkAccount}
              onOpenDuel={() => setTab("league")}
            />
          </Pad>
        </div>
      </main>
      <BottomNav tab={tab} setTab={setTab} isTester={me?.name === "aseo.bonjour"} />
      {reveal && (
        <RevealModal
          items={reveal.items}
          kind={reveal.kind}
          equippedBySlot={(me?.inventory ?? [])
            .filter((it) => me?.equipped?.[it.slot] === it.iid)
            .reduce<Record<string, any>>((acc, it) => { acc[it.slot] = it; return acc; }, {})
          }
          onClose={() => setReveal(null)}
        />
      )}
      {inspecting && (
        <PlayerInspectModal
          row={inspecting}
          myStats={derived?.stats ?? {}}
          myName={me.name}
          loading={inspectLoading}
          onClose={() => { inspectReq.current++; setInspectLoading(false); setInspecting(null); }}
        />
      )}

      {toast && <Toast msg={toast} />}
      {oauthError && (
        <div style={{ position: "fixed", top: 24, left: "50%", transform: "translateX(-50%)", zIndex: 9999, background: "#1e1e2e", border: "1px solid #f38ba8", borderRadius: 12, padding: "16px 24px", color: "#f38ba8", maxWidth: 380, textAlign: "center", fontSize: 14, boxShadow: "0 4px 24px #0008" }}>
          {oauthError}
          <button onClick={() => setOauthError(null)} style={{ display: "block", margin: "12px auto 0", padding: "6px 16px", borderRadius: 8, border: "none", background: "#f38ba8", color: "#1e1e2e", fontWeight: 700, cursor: "pointer" }}>OK</button>
        </div>
      )}

      {pendingModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "#1e1e2e", border: "1px solid #f9e2af", borderRadius: 16, padding: 24, maxWidth: 360, textAlign: "center", color: "#fff", boxShadow: "0 8px 40px #000a" }}>
            <div style={{ fontSize: 40, lineHeight: 1 }}>📦</div>
            <h3 style={{ margin: "10px 0 6px", fontSize: 18, fontWeight: 900 }}>Inventory full</h3>
            <p style={{ margin: 0, fontSize: 14, color: "#cdd6f4", lineHeight: 1.5 }}>
              You earned a reward but your inventory is full. Sell an item to free a slot — your reward drops in automatically.
            </p>
            <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
              <button onClick={() => setPendingModal(false)} style={{ flex: 1, padding: 10, borderRadius: 10, border: "1px solid #45475a", background: "transparent", color: "#cdd6f4", fontWeight: 700, cursor: "pointer" }}>Later</button>
              <button onClick={() => { setPendingModal(false); setTab("profil"); }} style={{ flex: 1, padding: 10, borderRadius: 10, border: "none", background: "#f9e2af", color: "#1e1e2e", fontWeight: 800, cursor: "pointer" }}>Free a slot</button>
            </div>
          </div>
        </div>
      )}

      {overlayQueue[0]?.kind === "bet" && (
        <BetResultScreen items={overlayQueue[0].items} onClose={closeOverlay} />
      )}

      {overlayQueue[0]?.kind === "levelup" && (
        <LevelUpScreen
          level={overlayQueue[0].level}
          gotTicket={overlayQueue[0].gotTicket}
          vitalityGain={overlayQueue[0].vitalityGain}
          onClose={closeOverlay}
        />
      )}

      {feedbackOpen && (
        <FeedbackModal
          profileId={profileId}
          playerName={me?.name}
          currentTab={tab}
          onClose={() => setFeedbackOpen(false)}
        />
      )}
    </Shell>
  );
}

/* ─── Chrome ─────────────────────────────────────────────────────────── */
const Pad = ({ children }: { children: React.ReactNode }) => (
  <div style={{ padding: "10px 14px 96px" }}>{children}</div>
);
const Shell = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      minHeight: "100vh",
      background: `radial-gradient(1200px 600px at 50% -10%,${C.bg2},${C.bg})`,
      fontFamily: "'Sora',system-ui,sans-serif",
      color: C.text,
    }}
  >
    <div
      style={{
        maxWidth: 460,
        margin: "0 auto",
        position: "relative",
        minHeight: "100vh",
      }}
    >
      {children}
    </div>
  </div>
);
function Style() {
  return (
    <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800;900&family=Archivo:wght@600;800;900&display=swap');
    html,body,#root{margin:0;padding:0;background:${C.bg};}
    body,*{font-family:'Sora',system-ui,sans-serif;}
    .d{font-family:'Sora',system-ui,sans-serif;}
    *{box-sizing:border-box;-webkit-tap-highlight-color:transparent;}
    button,input{font-family:inherit;}
    ::-webkit-scrollbar{width:0;height:0;}
    .ns{scrollbar-width:none;-ms-overflow-style:none;}
    @keyframes blink{0%,100%{opacity:1}50%{opacity:.2}}
    @keyframes bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-12px)}}
    @keyframes shake{0%,100%{transform:translate(0,0) rotate(0)}15%{transform:translate(-5px,2px) rotate(-2.5deg)}30%{transform:translate(6px,-2px) rotate(2.5deg)}45%{transform:translate(-7px,1px) rotate(-3deg)}60%{transform:translate(7px,-1px) rotate(3deg)}75%{transform:translate(-4px,2px) rotate(-2deg)}90%{transform:translate(4px,-2px) rotate(2deg)}}
    @keyframes burst{0%{transform:scale(.2);opacity:0}25%{opacity:1}100%{transform:scale(1.8);opacity:0}}
    @keyframes chestWhiten{0%{filter:drop-shadow(0 12px 32px rgba(255,255,255,.45)) brightness(1) saturate(1)}35%{filter:drop-shadow(0 0 35px #fff) brightness(3.2) saturate(.4)}100%{filter:drop-shadow(0 0 90px #fff) brightness(14) saturate(0)}}
    @keyframes chestSquash{0%{transform:scale(1)}12%{transform:scale(1.16,.82)}30%{transform:scale(.92,1.12) translateY(-8px)}55%{transform:scale(.5,.5)}78%{transform:scale(.16,.16)}100%{transform:scale(.04,.04);opacity:0}}
    @keyframes springOut{0%{transform:scale(.2);opacity:0}55%{transform:scale(1.12);opacity:1}75%{transform:scale(.96)}100%{transform:scale(1);opacity:1}}
    @keyframes flipIn{from{transform:rotateY(180deg)}to{transform:rotateY(0deg)}}
    @keyframes glowFd{from{opacity:0;transform:scale(.6)}to{opacity:1;transform:scale(1)}}
    @keyframes capPulse{0%,100%{filter:drop-shadow(0 0 6px currentColor)}50%{filter:drop-shadow(0 0 24px currentColor)}}
    @keyframes slideUp{from{transform:translateY(12px);opacity:0}to{opacity:1}}
    @keyframes spin{to{transform:rotate(360deg)}}
    @keyframes pageSlideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}
    @keyframes skeletonShimmer{0%{background-position:-220px 0}100%{background-position:220px 0}}
    .skel{background-color:rgba(255,255,255,.07);background-image:linear-gradient(90deg,rgba(255,255,255,.07) 25%,rgba(255,255,255,.20) 37%,rgba(255,255,255,.07) 63%);background-size:440px 100%;animation:skeletonShimmer 1.25s ease-in-out infinite;border-radius:6px}
    @media(prefers-reduced-motion:reduce){*{animation-duration:.01ms!important}}
  `}</style>
  );
}

function TopBar({
  displayedPoints,
  displayedGold,
  tickets = 0,
  onFeedback,
}: {
  displayedPoints: number;
  displayedGold: number;
  tickets?: number;
  onFeedback?: () => void;
}) {
  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 40,
        padding: "11px 14px 9px",
        background: `linear-gradient(${C.bg2},${C.bg2}E8)`,
        backdropFilter: "blur(8px)",
        borderBottom: `1px solid ${C.lineSoft}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 9,
              display: "grid",
              placeItems: "center",
              background: `linear-gradient(135deg,${C.turf},${C.turfDim})`,
            }}
          >
            ⚽
          </div>
          <div
            className="d"
            style={{ fontWeight: 900, fontSize: 18, letterSpacing: -0.3 }}
          >
            Prono<span style={{ color: C.turf }}>Cup</span>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            fontSize: 13,
          }}
        >
          {/* Tickets loges */}
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 900, color: "#C084FC" }}
            title="Loge Tickets — monnaie cosmétique"
          >
            <img src={TICKET_ICON} alt="🎫" style={{ width: 21, height: 21, objectFit: "contain" }} />
            {tickets}
          </span>
          <Coin n={displayedGold} />
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontWeight: 700,
            }}
          >
            <img src="https://pub-fe12241b7bb041a784ec4049e068ea80.r2.dev/PointIcon%20(1).png" alt="" style={{ width: 18, height: 18, objectFit: "contain" }} />{" "}
            {displayedPoints.toLocaleString("en-US")}
          </span>
          <button
            onClick={onFeedback}
            title="Send feedback"
            aria-label="Send feedback"
            style={{
              position: "relative",
              background: "rgba(255,255,255,0.07)",
              border: `1px solid ${C.line}`,
              borderRadius: 9,
              width: 32,
              height: 32,
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
              color: C.muted,
              flexShrink: 0,
            }}
          >
            {/* Smiley speech bubble: bubble outline + two eyes + smile curve */}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M20 4H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3v3l4-3h9a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <circle cx="9" cy="10" r="1.15" fill="currentColor" />
              <circle cx="15" cy="10" r="1.15" fill="currentColor" />
              <path
                d="M9 12.4c.7 1.4 5.3 1.4 6 0"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          </button>
        </div>
      </div>
    </header>
  );
}
function BottomNav({ tab, setTab }: { tab: string; setTab: (t: string) => void; isTester?: boolean }) {
  const { t } = useI18n();
  // Lobby = nouveau hub "Play" (bouton central surélevé). Standings reste
  // accessible via le bouton leaderboard du Lobby ; l'ancien hub league n'est plus
  // dans la nav (le Lobby le remplace). Ordre : Picks · Play · Shop · Profile.
  const items = [
    { k: "pronos", l: t("nav.pronos"), I: Gavel },
    { k: "lobby", l: "Play", I: Swords },
    { k: "packs", l: t("nav.shop"), I: Package },
    { k: "profil", l: t("nav.profile"), I: User },
  ];
  return (
    <nav
      style={{
        position: "fixed",
        bottom: 0,
        left: "50%",
        transform: "translateX(-50%)",
        width: "100%",
        maxWidth: 460,
        display: "flex",
        background: `${C.surface}F4`,
        backdropFilter: "blur(10px)",
        borderTop: `1px solid ${C.line}`,
        zIndex: 40,
      }}
    >
      {items.map(({ k, l, I }) => {
        const a = tab === k;
        const isPlay = k === "lobby";
        return (
          <button
            key={k}
            onClick={() => { setTab(k); window.scrollTo(0, 0); }}
            style={{
              flex: 1,
              background: "none",
              border: "none",
              padding: "9px 0 13px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              color: a ? C.turf : C.faint,
            }}
          >
            {isPlay ? (
              <div
                style={{
                  display: "grid",
                  placeItems: "center",
                  width: 34,
                  height: 34,
                  marginTop: -4,
                  borderRadius: "50%",
                  background: a ? C.turf : `${C.turf}1F`,
                  boxShadow: a
                    ? `0 0 16px ${C.turf}AA, 0 0 5px ${C.turf}`
                    : `0 0 11px ${C.turf}66`,
                  transition: "background .2s, box-shadow .2s",
                }}
              >
                <I size={19} color={a ? "#04140A" : C.turf} strokeWidth={2.6} />
              </div>
            ) : (
              <I size={20} strokeWidth={a ? 2.4 : 2} />
            )}
            <span
              style={{
                fontSize: 9.5,
                fontWeight: isPlay || a ? 800 : 600,
              }}
            >
              {l}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

/* ─── Onboarding ─────────────────────────────────────────────────────── */
function Onboarding({ onCreate }: { onCreate: (name: string) => void }) {
  const [name, setName] = useState("");
  return (
    <div
      style={{
        padding: "60px 22px",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        justifyContent: "center",
      }}
    >
      <div style={{ fontSize: 46, marginBottom: 6 }}>⚽🏆</div>
      <h1
        className="d"
        style={{
          fontSize: 32,
          fontWeight: 900,
          lineHeight: 1.05,
          margin: "4px 0 10px",
        }}
      >
        Prono<span style={{ color: C.turf }}>Cup</span>
      </h1>
      <p
        style={{
          color: C.muted,
          margin: "0 0 26px",
          fontSize: 14.5,
          lineHeight: 1.5,
        }}
      >
        Predict World Cup matches, climb the{" "}
        the standings, open packs and
        gear up your player.
      </p>
      <label
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: C.muted,
          marginBottom: 6,
        }}
      >
        YOUR TAG
      </label>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Sam, BugHunter…"
        onKeyDown={(e) => e.key === "Enter" && name.trim() && onCreate(name)}
        style={{
          background: C.surface,
          border: `1px solid ${C.line}`,
          borderRadius: 12,
          padding: "13px 14px",
          color: C.text,
          fontSize: 15,
          outline: "none",
          marginBottom: 16,
        }}
      />
      <button
        onClick={() => name.trim() && onCreate(name)}
        disabled={!name.trim()}
        style={{
          background: name.trim()
            ? `linear-gradient(135deg,${C.turf},${C.turfDim})`
            : C.surface,
          color: name.trim() ? "#062012" : C.faint,
          border: "none",
          borderRadius: 12,
          padding: 14,
          fontWeight: 900,
          fontSize: 15.5,
        }}
      >
        Let's go →
      </button>
      <p
        style={{
          color: C.faint,
          fontSize: 11.5,
          marginTop: 14,
          lineHeight: 1.5,
        }}
      >
        You get {STARTER_GOLD} coins + 1 free pack. Your tag is saved in your
        browser.
      </p>
    </div>
  );
}

type FeedEvent = {
  key: string; player: string; isMe: boolean; t1: string; t2: string;
  pick: string; winner: string; realScore: string; predictedScore: string | null;
  exactScore: boolean; placedAt: string;
};
function Friends({ myName }: { myName: string }) {
  const [feed, setFeed] = useState<FeedEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [rawBets, rawMatches] = await Promise.all([
        sb.get(
          "bets?select=player_name,match_id,pick,score_a,score_b,placed_at&order=placed_at.desc&limit=200"
        ),
        sb.get("matches?select=*"),
      ]);

      const matchMap = Object.fromEntries(
        (rawMatches || []).map((m) => [m.id, m])
      );

      const events: FeedEvent[] = [];
      (rawBets || []).forEach((b) => {
        const match = matchMap[b.match_id];
        if (!match || match.status !== "finished" || !match.result) return;
        if (b.pick !== match.result) return; // pari perdu, on skip

        const t1 = match.team1;
        const t2 = match.team2;
        const winner =
          match.result === "team1"
            ? t1
            : match.result === "team2"
            ? t2
            : "Draw";
        const realScoreA = match.team1_score;
        const realScoreB = match.team2_score;

        const exactScore =
          b.score_a != null &&
          b.score_b != null &&
          Number(b.score_a) === realScoreA &&
          Number(b.score_b) === realScoreB;

        events.push({
          key: `${b.player_name}-${b.match_id}`,
          player: b.player_name,
          isMe: b.player_name === myName,
          t1,
          t2,
          pick: b.pick,
          winner,
          realScore: `${realScoreA}-${realScoreB}`,
          predictedScore:
            b.score_a != null ? `${b.score_a}-${b.score_b}` : null,
          exactScore,
          placedAt: b.placed_at,
        });
      });

      setFeed(events);
      setLoading(false);
    })();
  }, []);

  const timeAgo = (ts: string) => {
    const diff = Date.now() - new Date(ts).getTime();
    const m = Math.floor(diff / 60000);
    const h = Math.floor(m / 60);
    const d = Math.floor(h / 24);
    if (d > 0) return `${d}d ago`;
    if (h > 0) return `${h}h ago`;
    if (m > 0) return `${m}min ago`;
    return "just now";
  };

  return (
    <div>
      <SH title="Friends Feed" sub="Team's winning bets" />
      {loading && (
        <div style={{ textAlign: "center", color: C.muted, padding: "40px 0" }}>
          Loading...
        </div>
      )}
      {!loading && feed.length === 0 && (
        <div
          style={{
            textAlign: "center",
            color: C.faint,
            padding: "40px 0",
            fontSize: 13,
          }}
        >
          No winning bets yet.
        </div>
      )}
      {feed.map((ev) => (
        <div
          key={ev.key}
          style={{
            background: ev.exactScore
              ? `linear-gradient(135deg,${C.gold}12,${C.surface})`
              : ev.isMe
              ? `${C.turf}0D`
              : C.surface,
            border: `1px solid ${
              ev.exactScore ? C.gold + "55" : ev.isMe ? C.turf + "44" : C.line
            }`,
            borderRadius: 13,
            padding: "12px 13px",
            marginBottom: 8,
            display: "flex",
            gap: 11,
            alignItems: "flex-start",
          }}
        >
          {/* Icône */}
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: ev.exactScore ? `${C.gold}22` : `${C.turf}18`,
              display: "grid",
              placeItems: "center",
              fontSize: 16,
              flexShrink: 0,
            }}
          >
            {ev.exactScore ? "🎯" : "✅"}
          </div>

          {/* Texte */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.4 }}>
              <span
                style={{ color: ev.isMe ? C.turf : C.text, fontWeight: 900 }}
              >
                {ev.isMe ? "You" : ev.player}
              </span>{" "}
              {ev.exactScore ? (
                <>
                  <span style={{ color: C.gold }}>exact score</span>{" "}
                  <span style={{ color: C.muted }}>
                    {ev.t1} {ev.predictedScore} {ev.t2}
                  </span>{" "}
                  🎯
                </>
              ) : (
                <>
                  <span style={{ color: C.muted }}>bet on</span>{" "}
                  <span style={{ color: C.turf }}>{ev.winner}</span>{" "}
                  <span style={{ color: C.muted }}>
                    ({ev.t1} vs {ev.t2} · {ev.realScore})
                  </span>
                </>
              )}
            </div>
            {ev.exactScore && (
              <div
                style={{
                  marginTop: 4,
                  fontSize: 11,
                  color: C.gold,
                  fontWeight: 800,
                }}
              >
                ×2 points — Exact score!
              </div>
            )}
            <div style={{ fontSize: 10.5, color: C.faint, marginTop: 3 }}>
              {timeAgo(ev.placedAt)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Match simulation engine ─────────────────────────────────────────── */
const HOME_TEAM = "Coupang Soccer",
  AWAY_TEAM = "Samsung Excel FC";
const BOT_F = [
  "Sergio",
  "Lucas",
  "Karim",
  "Antoine",
  "Rayan",
  "Théo",
  "Axel",
  "Noah",
  "Enzo",
  "Kylian",
  "Mohamed",
  "Alexis",
  "Nathan",
  "Quentin",
  "Florian",
  "Jonathan",
  "Dylan",
  "Kevin",
  "Samir",
  "Killian",
];
const BOT_L = [
  "Dupont",
  "Silva",
  "García",
  "Müller",
  "Kim",
  "Park",
  "Benzema",
  "Kanté",
  "Salah",
  "Gnabry",
  "Bellingham",
  "Vinícius",
  "Camavinga",
  "Thuram",
  "Dembélé",
  "Giroud",
  "Lloris",
  "Varane",
  "Digne",
  "Lemar",
];
const botN = (s: number) =>
  `${BOT_F[s % BOT_F.length]} ${BOT_L[Math.floor(s * 7 + 3) % BOT_L.length]}`;


/* ─── TestMatch UI ────────────────────────────────────────────────────── */
function TestMatch({ me, onEarnCoins, onEarnCup }: {
  me: Player;
  onEarnCoins: (n: number) => void;
  onEarnCup: () => void;
}) {
  const [phase, setPhase] = useState("setup");
  const [sim, setSim] = useState<ReturnType<typeof runSim> | null>(null);
  const [min, setMin] = useState(0);
  const [awarded, setAwarded] = useState(false);
  const [realPlayers, setRealPlayers] = useState<string[]>([]);
  const MS = 30000 / 90;

  useEffect(() => {
    sb.get("players?select=name&is_bot=eq.false&order=score.desc").then((rows) => {
      if (rows?.length) setRealPlayers(rows.map((r) => r.name));
    });
  }, []);

  const previewHome = useMemo(() => {
    const others = realPlayers.filter((n) => n !== me.name).slice(0, 9);
    const bots = Array.from(
      { length: Math.max(0, 10 - others.length) },
      (_, i) => botN(i + 50 + others.length)
    );
    return [me.name, ...others, ...bots];
  }, [me.name, realPlayers]);
  const previewAway = useMemo(
    () => Array.from({ length: 11 }, (_, i) => botN(i + 20)),
    []
  );

  const doLaunch = useCallback(() => {
    const d = runSim(me.name);
    setSim(d);
    setMin(0);
    setPhase("playing");
    setAwarded(false);
  }, [me.name, realPlayers]);

  useEffect(() => {
    if (phase !== "playing") return;
    const t = setInterval(() => setMin((m) => (m >= 90 ? 90 : m + 1)), MS);
    return () => clearInterval(t);
  }, [phase]);
  useEffect(() => {
    if (phase === "playing" && min >= 90) setPhase("ended");
  }, [min, phase]);
  useEffect(() => {
    if (phase !== "ended" || !sim || awarded) return;

    // 🔥 FIX : On utilise le vrai score calculé par la simu
    if (sim.hScore > sim.aScore) {
      onEarnCoins(500);
      // On commente temporairement onEarnCup() pour éviter le crash Supabase de la colonne manquante
      // onEarnCup();
      setAwarded(true);
    } else {
      // Si c'est un nul ou une défaite, on valide juste le fait que le match est traité
      setAwarded(true);
    }
  }, [phase, sim, awarded, onEarnCoins]);

  const vis = sim ? sim.evts.filter((e) => e.min <= min) : [];
  const liveH = vis.filter(
    (e) => e.type === "goal" && e.team === "home"
  ).length;
  const liveA = vis.filter(
    (e) => e.type === "goal" && e.team === "away"
  ).length;
  const livePen = vis.find((e) => e.type === "penalties");
  const pct = Math.round((min / 90) * 100);
  const evLabel = (ev: any) => {
    if (ev.type === "goal")
      return (
        "⚽ Goal by " +
        ev.scorer +
        (ev.assister ? " (" + ev.assister + ")" : "")
      );
    if (ev.type === "shot") return "🎯 On-target shot by " + ev.player;
    if (ev.type === "sub") return "\uD83D\uDD04 " + ev.out + " \u2192 " + ev.in;
    if (ev.type === "injury")
      return "🩹 Injury " + ev.severity + " \u2014 " + ev.player;
    if (ev.type === "penalties")
      return (
        "🥅 Penalties: " +
        HOME_TEAM.split(" ")[0] +
        " " +
        ev.sh +
        "\u2013" +
        ev.sa +
        " " +
        AWAY_TEAM.split(" ")[0]
      );
    return "";
  };
  const tCol = (ev: any) => (ev.team === "home" ? C.turf : "#e06a8c");
  const byMe = (ev: any) => ev.sH || ev.aH || ev.isH;
  const tomorrow = new Date(Date.now() + 86400000).toLocaleDateString("en-US", {
    timeZone: "Asia/Seoul",
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <div style={{ padding: "10px 14px 96px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 14,
          padding: "12px 14px",
          background:
            "linear-gradient(135deg," + C.surface2 + "," + C.surface + ")",
          border: "1px solid " + C.gold + "44",
          borderRadius: 14,
        }}
      >
        <div style={{ fontSize: 28 }}>\uD83C\uDFC6</div>
        <div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 800,
              color: C.gold,
              letterSpacing: 0.8,
              textTransform: "uppercase",
            }}
          >
            K League · Group Stage
          </div>
          <div
            className="d"
            style={{ fontWeight: 900, fontSize: 15, margin: "2px 0" }}
          >
            Next match vs {AWAY_TEAM}
          </div>
          <div
            style={{
              fontSize: 11.5,
              color: C.muted,
              textTransform: "capitalize",
            }}
          >
            {tomorrow} à 16:00 KST
          </div>
        </div>
      </div>
      <SH title="Test Match" sub="Offline simulation · 90 min = 30 sec" />

      {phase === "setup" && (
        <div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 14,
            }}
          >
            <TmCard
              title={HOME_TEAM}
              players={previewHome}
              accent={C.turf}
              myName={me.name}
            />
            <TmCard title={AWAY_TEAM} players={previewAway} accent="#e06a8c" />
          </div>
          <button
            onClick={doLaunch}
            style={{
              width: "100%",
              border: "none",
              borderRadius: 14,
              padding: "15px",
              fontWeight: 900,
              fontSize: 15,
              background:
                "linear-gradient(135deg," + C.turf + "," + C.turfDim + ")",
              color: "#062012",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 9,
              fontFamily: "inherit",
            }}
          >
            <Play size={18} /> Force launch (test mode)
          </button>
          <p
            style={{
              fontSize: 11,
              color: C.faint,
              textAlign: "center",
              marginTop: 8,
              lineHeight: 1.5,
            }}
          >
            Win = <b style={{ color: C.gold }}>+500 coins</b> +{" "}
            <b style={{ color: C.gold }}>1 🏆 K League</b> · No draw,
            penalties if tied
          </p>
        </div>
      )}

      {(phase === "playing" || phase === "ended") && sim && (
        <>
          <div
            style={{
              background:
                "linear-gradient(135deg," + C.surface2 + "," + C.surface + ")",
              border: "1px solid " + C.line,
              borderRadius: 16,
              padding: "16px",
              marginBottom: 12,
              textAlign: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: C.turf,
                  maxWidth: 110,
                  textAlign: "left",
                  lineHeight: 1.2,
                }}
              >
                {HOME_TEAM}
              </span>
              <div>
                <div
                  className="d"
                  style={{
                    fontWeight: 900,
                    fontSize: 46,
                    letterSpacing: 2,
                    color: C.text,
                  }}
                >
                  {liveH}
                  <span
                    style={{ color: C.faint, fontSize: 22, margin: "0 8px" }}
                  >
                    &ndash;
                  </span>
                  {liveA}
                </div>
                {livePen && (
                  <div
                    style={{ fontSize: 10.5, fontWeight: 800, color: C.gold }}
                  >
                    T.A.B. : {livePen.sh}&ndash;{livePen.sa}
                  </div>
                )}
              </div>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: "#e06a8c",
                  maxWidth: 110,
                  textAlign: "right",
                  lineHeight: 1.2,
                }}
              >
                {AWAY_TEAM}
              </span>
            </div>
            <div
              style={{
                height: 5,
                background: C.bg2,
                borderRadius: 999,
                overflow: "hidden",
                marginBottom: 5,
              }}
            >
              <div
                style={{
                  width: pct + "%",
                  height: "100%",
                  background:
                    "linear-gradient(90deg," + C.turf + "," + C.turfDim + ")",
                  transition: "width .28s linear",
                }}
              />
            </div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                color: phase === "ended" ? C.gold : C.muted,
              }}
            >
              {phase === "ended" ? "⏱ Final whistle" : min + "'"}
            </div>
          </div>
          {phase === "ended" && (
            <MEndCard sim={sim} awarded={awarded} me={me} onReplay={doLaunch} />
          )}
          <SH
            title="Match Feed"
            sub={vis.length + " event" + (vis.length !== 1 ? "s" : "")}
          />
          {vis.length === 0 && (
            <div
              style={{
                color: C.faint,
                fontSize: 12.5,
                textAlign: "center",
                padding: "16px 0",
              }}
            >
              Kickoff — events will appear here
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {[...vis].reverse().map((ev, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 10px",
                  background: C.surface,
                  border:
                    "1px solid " +
                    (ev.type === "goal"
                      ? tCol(ev) + "66"
                      : ev.type === "penalties"
                      ? C.gold + "55"
                      : byMe(ev)
                      ? C.turf + "44"
                      : C.line),
                  borderRadius: 10,
                  animation: i === 0 ? "slideUp .3s ease" : "none",
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: C.faint,
                    minWidth: 24,
                    textAlign: "right",
                  }}
                >
                  {ev.min}'
                </span>
                <span
                  style={{
                    flex: 1,
                    fontSize: 12.5,
                    fontWeight:
                      ev.type === "goal" || ev.type === "penalties"
                        ? 800
                        : byMe(ev)
                        ? 700
                        : 500,
                    color:
                      ev.type === "goal"
                        ? tCol(ev)
                        : ev.type === "penalties"
                        ? C.gold
                        : byMe(ev)
                        ? C.turf
                        : C.text,
                  }}
                >
                  {evLabel(ev)}
                  {byMe(ev) && (
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 900,
                        color: C.turf,
                        marginLeft: 6,
                        background: C.turf + "1A",
                        padding: "1px 5px",
                        borderRadius: 4,
                      }}
                    >
                      ⭐ YOU
                    </span>
                  )}
                </span>
                {ev.team && ev.type !== "penalties" && (
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: tCol(ev),
                      flexShrink: 0,
                    }}
                  >
                    {ev.team === "home"
                      ? HOME_TEAM.split(" ")[0]
                      : AWAY_TEAM.split(" ")[0]}
                  </span>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
function TmCard({ title, players, accent, myName }: {
  title: string; players: string[]; accent: string; myName?: string;
}) {
  return (
    <div
      style={{
        background: C.surface,
        border: `1px solid ${accent}44`,
        borderRadius: 13,
        padding: "11px 10px",
        maxHeight: 280,
        overflowY: "auto",
      }}
      className="ns"
    >
      <div
        className="d"
        style={{
          fontWeight: 900,
          fontSize: 11.5,
          color: accent,
          marginBottom: 8,
          lineHeight: 1.2,
        }}
      >
        {title}
      </div>
      {players.map((p, i) => (
        <div
          key={i}
          style={{
            fontSize: 10.5,
            padding: "2px 0",
            display: "flex",
            alignItems: "center",
            gap: 5,
            color: p === myName ? C.turf : C.muted,
            fontWeight: p === myName ? 800 : 400,
          }}
        >
          {p === myName && (
            <span
              style={{
                fontSize: 8,
                background: C.turf,
                color: "#062012",
                borderRadius: 3,
                padding: "0 3px",
                fontWeight: 900,
                flexShrink: 0,
              }}
            >
              ME
            </span>
          )}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {p}
          </span>
        </div>
      ))}
    </div>
  );
}
function MEndCard({ sim, awarded, me, onReplay }: {
  sim: ReturnType<typeof runSim>; awarded: boolean; me: Player; onReplay: () => void;
}) {
  const isWin = sim.hScore > sim.aScore,
    isDraw = sim.hScore === sim.aScore;
  const rc = (r: number) =>
    r >= 8 ? C.gold : r >= 6 ? C.turf : r >= 5 ? C.muted : "#e06a8c";
  const rl = (r: number) =>
    r >= 9
      ? "Legendary ⚡"
      : r >= 8
      ? "Excellent 🔥"
      : r >= 7
      ? "Very good 👍"
      : r >= 6
      ? "Good 🙂"
      : r >= 5
      ? "Decent 😐"
      : "Poor 😬";
  return (
    <div
      style={{
        background: `linear-gradient(135deg,${
          isWin ? `${C.turf}18` : isDraw ? `${C.line}44` : "#e06a8c18"
        },${C.surface})`,
        border: `1px solid ${
          isWin ? `${C.turf}55` : isDraw ? C.line : "#e06a8c55"
        }`,
        borderRadius: 16,
        padding: "16px 14px",
        marginBottom: 14,
      }}
    >
      <div
        className="d"
        style={{
          fontWeight: 900,
          fontSize: 22,
          textAlign: "center",
          color: isWin ? C.turf : isDraw ? C.muted : "#e06a8c",
          marginBottom: 3,
        }}
      >
        {isWin ? "Victory! 🏆" : isDraw ? "Draw 🤝" : "Defeat ❌"}
      </div>
      <div
        style={{
          textAlign: "center",
          fontSize: 11.5,
          color: C.faint,
          marginBottom: 14,
        }}
      >
        Final score: {sim.hScore}–{sim.aScore}
      </div>

      <div
        style={{
          background: C.bg2,
          borderRadius: 12,
          padding: "12px",
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 9.5,
            fontWeight: 800,
            color: C.faint,
            letterSpacing: 0.5,
            marginBottom: 8,
            textTransform: "uppercase",
          }}
        >
          Your performance — {me.name}
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          {[
            ["⚽", sim.hG, "Goals"],
            ["🎯", sim.hA, "Assists"],
            ["🔧", sim.hSh, "Shots"],
          ].map(([ico, v, l]) => (
            <div
              key={l}
              style={{
                flex: 1,
                background: C.surface,
                borderRadius: 9,
                padding: "8px 4px",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 20 }}>{ico}</div>
              <div
                className="d"
                style={{
                  fontWeight: 900,
                  fontSize: 20,
                  color: v > 0 ? C.turf : C.faint,
                }}
              >
                {v}
              </div>
              <div
                style={{
                  fontSize: 9,
                  color: C.faint,
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                {l}
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              flex: 1,
              height: 8,
              background: C.bg,
              borderRadius: 999,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${(sim.rating / 10) * 100}%`,
                height: "100%",
                background: `linear-gradient(90deg,${rc(sim.rating)},${rc(
                  sim.rating
                )}BB)`,
                transition: "width .8s ease",
              }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
            <span
              className="d"
              style={{ fontWeight: 900, fontSize: 26, color: rc(sim.rating) }}
            >
              {sim.rating}
            </span>
            <span style={{ fontSize: 11, color: C.faint }}>/10</span>
          </div>
          <span
            style={{ fontSize: 11.5, fontWeight: 800, color: rc(sim.rating) }}
          >
            {rl(sim.rating)}
          </span>
        </div>
      </div>

      {isWin && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "9px",
            background: `${C.gold}15`,
            border: `1px solid ${C.gold}44`,
            borderRadius: 10,
            marginBottom: 10,
          }}
        >
          <Coins size={16} color={C.gold} />
          <span style={{ fontWeight: 800, fontSize: 13.5, color: C.gold }}>
            +500 coins added to your wallet!
          </span>
        </div>
      )}

      <button
        onClick={onReplay}
        style={{
          width: "100%",
          border: "none",
          borderRadius: 11,
          padding: "12px",
          fontWeight: 900,
          fontSize: 14,
          background: `linear-gradient(135deg,${C.turf},${C.turfDim})`,
          color: "#062012",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 7,
          fontFamily: "inherit",
        }}
      >
        <RefreshCw size={15} /> Play again
      </button>
    </div>
  );
}

